import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { SectionCaptureTable, CapturedItemCard } from "./ReviewSave";
import ChiefComplaints from "../chiefComplaints/ChiefComplaints";
import ComplaintForm from "../chiefComplaints/ComplaintForm";
import { CC_V2_ADD, CC_V2_EDIT, CC_V2_EDIT_CAPTURED } from "../chiefComplaints/events";
import Diagnosis from "../diagnosis/Diagnosis";
import DiagnosisForm from "../diagnosis/DiagnosisForm";
import { DX_V2_ADD, DX_V2_STATUS, DX_V2_EDIT_CAPTURED } from "../diagnosis/events";
import Medicines from "../medicines/Medicines";
import MedicineForm from "../medicines/MedicineForm";
import { MED_V2_ADD, MED_V2_STATUS, MED_V2_EDIT_CAPTURED } from "../medicines/events";
import MedicineStatusForm from "../medicines/MedicineStatusForm";
import Hospitalisation from "../hospitalisation/Hospitalisation";
import HospitalisationForm from "../hospitalisation/HospitalisationForm";
import { HOSP_V2_ADD, HOSP_V2_EDIT, HOSP_V2_EDIT_CAPTURED } from "../hospitalisation/events";
import LabInvestigations from "../labInvestigations/LabInvestigations";
import LabInvestigationForm from "../labInvestigations/LabInvestigationForm";
import { LI_V2_EDIT } from "../labInvestigations/events";
import Allergies from "../allergies/Allergies";
import AllergyForm from "../allergies/AllergyForm";
import { ALG_V2_ADD, ALG_V2_EDIT, ALG_V2_EDIT_CAPTURED } from "../allergies/events";
import FamilyHistory from "../familyHistory/FamilyHistory";
import RelativeForm from "../familyHistory/RelativeForm";
import { FAM_V2_ADD, FAM_V2_EDIT, FAM_V2_EDIT_CAPTURED } from "../familyHistory/events";
import { isSingleRelationship } from "../familyHistory/data";
import Addictions from "../addictions/Addictions";
import AddictionForm from "../addictions/AddictionForm";
import { ADD_V2_EDIT } from "../addictions/events";
import PhysicalActivity from "../physicalActivity/PhysicalActivity";
import ActivityForm from "../physicalActivity/ActivityForm";
import { ACT_V2_ADD, ACT_V2_EDIT, ACT_V2_STATUS, ACT_V2_EDIT_CAPTURED } from "../physicalActivity/events";
import Anthropometric from "../anthropometric/Anthropometric";
import AnthropometricForm from "../anthropometric/AnthropometricForm";
import { ANTH_V2_SELECT_PARAM } from "../anthropometric/events";
import DailySchedule from "../dailySchedule/DailySchedule";
import ScheduleEntryForm from "../dailySchedule/ScheduleEntryForm";
import FoodPreference from "../foodPreference/FoodPreference";
import { FP_V2_AI_INJECT } from "../foodPreference/events";
import DrugSchedule from "../drugSchedule/DrugSchedule";
import DrugScheduleEditor from "../drugSchedule/DrugScheduleEditor";
import PatientDetails from "../patientDetails/PatientDetails";
import PatientDetailsEditor from "../patientDetails/PatientDetailsEditor";
import { bodyToCapturedUpdate as patientDetailsBodyToCapturedUpdate } from "./patientDetailsBodyAdapters";
import { DS_V2_EDIT_CAPTURED, DS_V2_SHEET_COUNT } from "../drugSchedule/events";
import { stagedAddToPlan as dsStagedAddToPlan } from "../drugSchedule/helpers";
import focusFirstFocusable from "../common/focusFirstFocusable";
import { bodyToCapturedAdd as drugScheduleBodyToCapturedAdd } from "./drugScheduleBodyAdapters";
import {
  bodyToCapturedAdd as ccBodyToCapturedAdd,
  bodyToCapturedUpdate as ccBodyToCapturedUpdate,
} from "./complaintBodyAdapters";
import {
  bodyToCapturedAdd as dxBodyToCapturedAdd,
  bodyToCapturedUpdate as dxBodyToCapturedUpdate,
} from "./diagnosisBodyAdapters";
import {
  bodyToCapturedAdd as medBodyToCapturedAdd,
} from "./medicineBodyAdapters";
import {
  bodyToCapturedAdd as hospBodyToCapturedAdd,
  bodyToCapturedUpdate as hospBodyToCapturedUpdate,
} from "./hospitalisationBodyAdapters";
import {
  bodyToCapturedAdd as labBodyToCapturedAdd,
  capturedItemsToCardEditing as labCapturedItemsToCardEditing,
  commitToPrimaryCapturedAdd as labCommitToPrimaryCapturedAdd,
} from "./labInvestigationBodyAdapters";
import DigitiseManuallyCard from "../labInvestigations/DigitiseManuallyCard";
import {
  bodyToCapturedAdd as algBodyToCapturedAdd,
  bodyToCapturedUpdate as algBodyToCapturedUpdate,
} from "./allergyBodyAdapters";
import {
  bodyToCapturedAdd as fhBodyToCapturedAdd,
  bodyToCapturedUpdate as fhBodyToCapturedUpdate,
} from "./familyHistoryBodyAdapters";
import {
  bodyToCapturedAdd as addBodyToCapturedAdd,
} from "./addictionBodyAdapters";
import {
  bodyToCapturedAdd as actBodyToCapturedAdd,
  bodyToCapturedUpdate as actBodyToCapturedUpdate,
} from "./activityBodyAdapters";
import {
  bodyToCapturedAdd as anthBodyToCapturedAdd,
} from "./anthropometricBodyAdapters";
import {
  bodyToCapturedAdd as dsBodyToCapturedAdd,
} from "./dailyScheduleBodyAdapters";
/**
 * S2 middle+right region of the AI-transcript modal.
 *
 * Layout matches spec board 2495:3060 — a horizontal tab strip at the
 * top, then a split below:
 *
 *   ┌─────────── section tabs ────────────────────────┐
 *   ├───────── table ─────────┬── cards column ───────┤
 *   │  Added items for the    │  Proposed items for   │
 *   │  active tab             │  the active tab       │
 *   │  (accepted by care mgr) │  (awaiting Add /      │
 *   │                         │   Discard)            │
 *   └─────────────────────────┴───────────────────────┘
 *
 * Items are split by state:
 *   proposed        → right column, as a review card with Add + Discard
 *   added_in_call   → middle table, as a row
 *   edited_in_call  → middle table, as a row
 *   discarded_*     → hidden (audit lives in the transcript doc)
 *
 * Per-tab badge counts count PROPOSED items only — the number that need
 * a decision. Added rows aren't attention-worthy after acceptance.
 *
 * Per-section rich forms (VAS status, Duration steppers, Consulted-
 * doctor toggle) are deliberately NOT rendered yet — this slice ships
 * a generic table + generic cards, matching the mock's shape without
 * the per-section columns. Section-specific rendering is Phase 2c.
 */

// Kebab-case tab id ↔ snake-case Go section string. Includes the full
// 12 v2 sections; missing ones map to null.
export const TAB_TO_SECTION = {
  "patient-details":   "patient_details",
  "anthropometric":    "anthropometric",
  "chief-complaints":  "chief_complaints",
  "diagnosis":         "diagnosis",
  "medicines":         "medicines",
  "hospitalisation":   "hospitalisation",
  "allergies":         "allergies",
  "family-history":    "family_history",
  "addictions":        "addictions",
  "lab-investigations": "lab_investigations",
  "food-preferences":  "food_preferences",
  "daily-schedule":    "daily_schedule",
  "activity":          "activity",
  "drug-schedule":     "drug_schedule",
};

// Human-facing section name for the table header ("Chief complaints · 3 entries").
const SECTION_HEADING = {
  patient_details:   "Patient details",
  chief_complaints:  "Chief complaints",
  diagnosis:         "Diagnosis",
  medicines:         "Medicines",
  hospitalisation:   "Hospitalisation history",
  allergies:         "Allergies",
  family_history:    "Family history",
  addictions:        "Addictions",
  anthropometric:    "Anthropometric data",
  lab_investigations: "Lab investigations",
  food_preferences:  "Food preferences",
  daily_schedule:    "Daily schedule",
  activity:          "Physical activity",
  drug_schedule:     "Drug schedule",
};

// Best-guess "title" field per section. Falls back to payload.value if
// the specific field is missing.
const TITLE_FIELD = {
  chief_complaints: "complaint",
  diagnosis:        "name",
  medicines:        "name",
  hospitalisation:  "name",
  allergies:        "name",
  family_history:   "condition",
  addictions:       "substance",
};

const titleFor = (item) => {
  const t = item && item.payload && item.payload[TITLE_FIELD[item.targetSection]];
  return (t && String(t)) || (item && item.payload && item.payload.value) || "(untitled)";
};

// Compact meta line — code + severity + duration if present. Same idea
// as the previous inline card meta.
const metaFor = (item) => {
  const p = (item && item.payload) || {};
  const bits = [];
  if (p.icd_code)        bits.push(`ICD ${p.icd_code}`);
  else if (p.cpt_code)   bits.push(`CPT ${p.cpt_code}`);
  else if (p.loinc_code) bits.push(`LOINC ${p.loinc_code}`);
  if (p.since_when) bits.push(`since ${p.since_when}`);
  if (p.intensity)  bits.push(p.intensity);
  return bits.join(" · ");
};

// Payload fields already surfaced in title + meta; skip in the "other
// fields" list so we don't repeat info.
const HIDE_KEYS = new Set([
  "complaint", "name", "condition", "substance", "value",
  "icd_code", "cpt_code", "loinc_code",
  "since_when", "intensity",
  "icd_description", "cpt_description", "loinc_description",
  "confidence", "evidence",
  "icd_status", "icd_original_code", "icd_curated_code",
  "loinc_status", "loinc_llm_original_code", "loinc_llm_original_description",
  "cpt_status", "cpt_llm_original_code", "cpt_llm_original_description",
  "id", "field", "review_status", "timestamp", "edited",
]);

const otherFields = (payload) => {
  if (!payload) return [];
  return Object.keys(payload)
    .filter((k) => !HIDE_KEYS.has(k) && payload[k] != null && payload[k] !== "")
    .map((k) => ({ k, v: String(payload[k]) }));
};

// ─── Right-column proposal preview card ─────────────────────────────
//
// Rendered for items in state="proposed". Read-only during the call —
// the care manager reviews these visually while the conversation runs
// but takes NO action here. Accept/Discard/Edit all move to the review
// phase (post-call). The eyebrow shows the target section + code family.
const ProposalCard = ({ item }) => {
  const others = otherFields(item.payload);
  const meta   = metaFor(item);
  return (
    <div className="aic-proposal">
      <div className="aic-proposal-head">
        <span className="aic-proposal-eyebrow">
          {SECTION_HEADING[item.targetSection] || item.targetSection}
          {item.payload && item.payload.icd_code ? " · ICD" : ""}
          {item.payload && item.payload.cpt_code ? " · CPT" : ""}
          {item.payload && item.payload.loinc_code ? " · LOINC" : ""}
        </span>
        <span className="aic-badge is-proposed">New</span>
      </div>
      <div className="aic-proposal-body">
        <p className="aic-proposal-title">{titleFor(item)}</p>
        {meta ? <p className="aic-proposal-meta">{meta}</p> : null}
        {others.length ? (
          <ul className="aic-proposal-kv">
            {others.map(({ k, v }) => (
              <li key={k}><span>{k}</span><em>{v}</em></li>
            ))}
          </ul>
        ) : null}
        {item.evidence ? (
          <p className="aic-proposal-evidence">“{item.evidence}”</p>
        ) : null}
      </div>
    </div>
  );
};
ProposalCard.propTypes = {
  item: PropTypes.object.isRequired,
};

// ─── Middle table row ───────────────────────────────────────────────
//
// Rendered for items in state="added_in_call" or "edited_in_call". Uses
// a generic 3-column layout (Title | Meta | Evidence) that reads
// reasonably across every section. Per-section columns (Chief
// complaints: Since when + Description + Status + Analysis) land in
// Phase 2c.
const TableRow = ({ item }) => (
  <div className="aic-trow" role="row">
    <span className="aic-tcell aic-trow-title" role="cell">{titleFor(item)}</span>
    <span className="aic-tcell aic-trow-meta"  role="cell">{metaFor(item) || "—"}</span>
    <span className="aic-tcell aic-trow-ev"    role="cell">
      {item.evidence ? `“${item.evidence}”` : "—"}
    </span>
  </div>
);
TableRow.propTypes = { item: PropTypes.object.isRequired };

// ─── Whole component ────────────────────────────────────────────────
//
// `patient` is threaded through so the Chief Complaints branch can hand
// it to the standalone `ChiefComplaints` component — the same component
// the sidebar section renders. The standalone owns its own CC v2 fetch;
// this file just embeds it.
// Standalone tab-strip renderer — extracted so AiTranscriptModal can
// render the tabs at the modal level (spanning the full grid width),
// leaving CapturedSections responsible for just the split content
// below. Pass `proposedCounts` when the caller has already computed
// per-section proposed counts; else omit and every tab renders without
// its numeric pill.
export const SectionTabs = ({ tabs, activeTab, onSelect, openCounts, modifiedSections }) => (
  <div className="aic-tabs" role="tablist">
    {tabs.map((t) => {
      const section = TAB_TO_SECTION[t.id];
      const n = section && openCounts ? openCounts[section] || 0 : 0;
      const isModified = !!(section && modifiedSections && modifiedSections.has(section));
      // Leading dot — a single orange marker whenever the section has
      // ANY activity for this call: at least one open card (`n > 0`) OR
      // a captured item (`isModified`). Earlier we drew a maroon dot
      // for the open-card-only state; reviewer feedback flagged the
      // two colours as inconsistent, so both cases now share the
      // orange treatment. Empty sections still render no dot.
      const showDot = isModified || n > 0;
      const extraCls = [
        t.id === activeTab ? "is-active" : "",
        n > 0 ? "has-open" : "",
        isModified ? "has-captures" : "",
      ].filter(Boolean).join(" ");
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={t.id === activeTab}
          className={`aic-tab${extraCls ? ` ${extraCls}` : ""}`}
          onClick={() => onSelect(t.id)}
        >
          {showDot ? <span className="aic-tab-dot is-modified" aria-hidden="true" /> : null}
          {t.label}
          {n ? <span className="aic-tab-count">{n}</span> : null}
        </button>
      );
    })}
  </div>
);
SectionTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id:    PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  activeTab:  PropTypes.string.isRequired,
  onSelect:   PropTypes.func.isRequired,
  openCounts: PropTypes.object,
  /** Set of targetSection keys ("chief_complaints", "medicines", …)
   *  that carry at least one non-discarded captured item for the
   *  current transcript. Renders the orange "modified" dot on those
   *  section tabs. */
  modifiedSections: PropTypes.instanceOf(Set),
};
SectionTabs.defaultProps = { openCounts: null, modifiedSections: null };

// Compute per-section counts of items still in "proposed" state — used
// as the AI half of the tab counter. The full "open cards" number
// (proposed AI items + manually-opened editors currently on screen)
// lives inside CapturedSections since it also owns the manual /
// update / re-edit editor arrays; that value bubbles up via
// onOpenCountsChange.
export const proposedCountsByCapturedItems = (items) =>
  (items || []).reduce((acc, it) => {
    if (!it || it.state !== "proposed") return acc;
    if (!it.targetSection) return acc;
    acc[it.targetSection] = (acc[it.targetSection] || 0) + 1;
    return acc;
  }, {});

const CapturedSections = ({
  tabs,
  activeTab,
  onSelectTab,
  items,
  patient,
  awaitingConsent,
  callEnded,
  patientId,
  appointmentId,
  transcriptId,
  transcriptService,
  version,
  onVersionSync,
  onItemsSync,
  // When true, the internal tab strip isn't rendered — caller draws
  // it externally (typically at the modal level so tabs can span
  // above the rail). Default false preserves the standalone shape.
  hideTabs,
  // "" = default (live-call / review flow — middle column renders
  // the standalone section component with captured items overlaid).
  // "review" = past-transcript view — middle column renders only
  // the items THIS transcript captured, using the shared S5 Review
  // tables. Right column collapses (no manual add / AI proposal
  // affordance when browsing a reopened transcript).
  viewMode,
  // Called with `{[section]: openCardCount}` whenever the set of
  // open cards changes. Modal uses it to (a) feed the SectionTabs
  // "● Label N" pill and (b) gate the Review call button on all
  // counts being zero after call-end.
  onOpenCountsChange,
}) => {
  const activeSection = TAB_TO_SECTION[activeTab] || null;

  // Local state for manual "+ Add complaints" blank cards. Each entry is
  // a plain string id — no data yet. On Save the card resolves + drops.
  const [manualCardIds, setManualCardIds] = useState([]);

  // ═════════════════════════════════════════════════════════════════
  //  LOCKED — Transcript-mode "no second blank card" rule (2026-09-21)
  // ═════════════════════════════════════════════════════════════════
  //  Standalone rails route "+ Add" through useSheetStack, which
  //  refuses to push a second blank sheet on top of an existing blank
  //  one AND pulses the blank sheet so the user can spot it in a tall
  //  stack. Transcript mode manages manual cards via per-section
  //  arrays here, so the same rule (with the same visual) has to live
  //  at this layer. Every manual form reports its filled state
  //  through `onCardFilledChange(cardId, bool)`; openManual handlers
  //  call `refuseAsBlank(cardId)` when a click is refused so the
  //  target sheet pulses (and scrolls into view). The pulse span is
  //  remounted on each refusal via a counter so the CSS animation
  //  genuinely replays.
  //
  //  Locked components (do NOT weaken silently — confirm with the
  //  owner before touching):
  //    (a) cardFilledRef + onCardFilledChange — the shared filled-map.
  //    (b) findBlankCardId / hasBlankManualCard — the guard predicate.
  //    (c) refuseAsBlank + the `nudge` state — the refusal pulse.
  //    (d) Every openManual handler across the 8 covered sections
  //        (CC, Dx, Med, Hosp, Lab, Alg, FH, Activity) using the
  //        `const blankId = findBlankCardId(prev); if (blankId) {
  //        refuseAsBlank(blankId); return prev; }` pattern.
  //    (e) DigitiseManuallyCard's onFilledChange effect (lab's cards
  //        would otherwise be permanently invisible to the guard).
  //    (f) Every manual-card render site is wrapped in a `.zy-sheet`
  //        div with `ref={registerCardNode}` + a conditional pulse
  //        `<span key={nudge.n} className="zy-sheet-pulse" ... />` —
  //        removing either kills the pulse animation.
  //
  //  See memory file `feedback_transcript_blank_guard_locked.md` for
  //  the full decision record. If a change genuinely needs to remove
  //  or weaken this behavior, surface it BEFORE editing — do not
  //  quietly delete the guard or the pulse wrapper.
  const cardFilledRef = useRef({});
  const onCardFilledChange = useCallback((cardId, filled) => {
    if (filled) {
      cardFilledRef.current[cardId] = true;
    } else {
      delete cardFilledRef.current[cardId];
    }
  }, []);
  const [nudge, setNudge] = useState({ id: null, n: 0 });
  const nudgeTimer = useRef(null);
  // cardId -> DOM node (so a refused click can scroll the pulsed card
  // into view). Written on each manual card's wrapper ref callback.
  const cardNodeRef = useRef({});
  // id of the card whose DOM node should grab focus on next mount.
  // Every openManual / openUpdate / openReedit handler wraps the fresh
  // cardId in `withPendingFocus(...)` so the reviewer's keystroke
  // lands in the new card (matches the standalone useSheetStack
  // pattern). Ref, not state: focus timing must be synchronous with
  // the ref callback.
  const pendingFocusRef = useRef(null);
  const withPendingFocus = useCallback((cardId) => {
    pendingFocusRef.current = cardId;
    return cardId;
  }, []);
  const registerCardNode = useCallback((cardId, node) => {
    if (node) {
      cardNodeRef.current[cardId] = node;
      if (pendingFocusRef.current === cardId) {
        pendingFocusRef.current = null;
        focusFirstFocusable(node);
      }
    } else {
      delete cardNodeRef.current[cardId];
    }
  }, []);
  const refuseAsBlank = useCallback((cardId) => {
    if (!cardId) return;
    setNudge((p) => ({ id: cardId, n: p.n + 1 }));
    const node = cardNodeRef.current[cardId];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest" });
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    nudgeTimer.current = setTimeout(() => {
      setNudge((p) => ({ id: null, n: p.n }));
    }, 1200);
  }, []);
  useEffect(() => () => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
  }, []);
  const findBlankCardId = useCallback(
    (ids) => (ids || []).find((id) => !cardFilledRef.current[id]) || null,
    [],
  );
  const hasBlankManualCard = useCallback((ids) => {
    return !!findBlankCardId(ids);
  }, [findBlankCardId]);

  // Update cards — care manager clicked a status chip on an existing
  // (untouched) CC v2 complaint. Each entry is {cardId, complaint} so
  // we can render an update-mode form pre-filled with that record.
  const [updateCards, setUpdateCards] = useState([]);
  // Re-edit cards — care manager clicked a status chip on a row that
  // already has a staged captured item (peach bg). Each entry is
  // {cardId, captured, ccv2Record} — the same underlying captured item
  // gets its payload updated via decideCaptured on Save.
  const [reeditCards, setReeditCards] = useState([]);

  // Diagnosis parallel state — same shape, per-section arrays so cards
  // don't cross-render when the reviewer switches tabs. Later refactor
  // once sections 3+ come online: promote to a single generic array
  // keyed by section.
  const [manualCardIdsDx, setManualCardIdsDx] = useState([]);
  const [updateCardsDx, setUpdateCardsDx] = useState([]);
  const [reeditCardsDx, setReeditCardsDx] = useState([]);

  // Medicines — Wave A first-cut only has ADD-side state. Update-side
  // (MedicineStatusForm) integration lands in Wave A pass 2 alongside
  // agent update auto-routing on name/mCode match.
  const [manualCardIdsMed, setManualCardIdsMed] = useState([]);
  const [reeditCardsMed, setReeditCardsMed] = useState([]);
  // Update cards for existing Medicine v2 rows the reviewer clicked in
  // the middle-column table (medicine name / STATUS chip). Mirrors CC's
  // updateCards shape: {cardId, medicine, mode}. mode="status" opens
  // MedicineStatusForm; other modes are Wave-2.
  const [updateCardsMed, setUpdateCardsMed] = useState([]);

  // Patient details — single-record UPDATE section. Rather than
  // relying on PD_V2_EDIT through the event bus (which stops the
  // right-column form from opening reliably when the standalone
  // shell's own PatientDetailsEditor also listens), we own the
  // Editor's open + focus state here and pass them down as
  // controlled props. Only ONE editing session ever exists at a
  // time — matches the single-record semantics.
  const [patientDetailsOpen, setPatientDetailsOpen] = useState(false);
  const [patientDetailsFocus, setPatientDetailsFocus] = useState(null);
  /* The row's click token — see PatientDetails.onRowClick. Held here because
     this mode owns the rail's state, and without it a second click on the same
     row focuses nothing. */
  const [patientDetailsFocusAt, setPatientDetailsFocusAt] = useState(0);
  const openPatientDetailsRail = ({ focusField, focusAt } = {}) => {
    setPatientDetailsFocus(focusField || null);
    setPatientDetailsFocusAt(focusAt || 0);
    setPatientDetailsOpen(true);
  };
  const closePatientDetailsRail = () => {
    setPatientDetailsOpen(false);
    setPatientDetailsFocus(null);
  };

  // Hospitalisation — same first-cut shape as Meds. Only manual add + AI
  // proposal + re-edit-of-staged-add. Update card materialises when the
  // reviewer clicks an existing record from the middle-column table.
  const [manualCardIdsHosp, setManualCardIdsHosp] = useState([]);
  const [reeditCardsHosp, setReeditCardsHosp] = useState([]);
  const [updateCardsHosp, setUpdateCardsHosp] = useState([]);

  // Lab investigations — same first-cut shape.
  const [manualCardIdsLab, setManualCardIdsLab] = useState([]);
  const [reeditCardsLab, setReeditCardsLab] = useState([]);

  // Allergies — Wave B first-cut. Manual add + AI proposal + re-edit of
  // staged add. Update card materialises when the reviewer clicks an
  // existing allergy row in the middle-column table.
  const [manualCardIdsAlg, setManualCardIdsAlg] = useState([]);
  const [reeditCardsAlg, setReeditCardsAlg] = useState([]);
  const [updateCardsAlg, setUpdateCardsAlg] = useState([]);

  // Family history — same first-cut shape as Allergies.
  const [manualCardIdsFh, setManualCardIdsFh] = useState([]);
  const [reeditCardsFh, setReeditCardsFh] = useState([]);
  const [updateCardsFh, setUpdateCardsFh] = useState([]);

  // Addictions — fixed 4-slot resource, no "add" concept. The user
  // opens an update card by clicking a row's name/chip in the embedded
  // Addictions component, which emits ADD_V2_EDIT with the persisted
  // row as `editing`. AI proposals + review re-edits reuse the same
  // form. `updateCardsAdd` carries {cardId, addiction} pairs.
  const [updateCardsAdd, setUpdateCardsAdd] = useState([]);
  const [reeditCardsAdd, setReeditCardsAdd] = useState([]);

  // Physical activity — same first-cut shape as Allergies / FH.
  const [manualCardIdsAct, setManualCardIdsAct] = useState([]);
  const [reeditCardsAct, setReeditCardsAct] = useState([]);
  const [updateCardsAct, setUpdateCardsAct] = useState([]);
  // Base v2 lists per section, bubbled up by the standalone components
  // via their onRowsSync callback. Used to detect exact-name matches
  // between AI proposals and existing patient rows so a proposal for
  // an entry the patient already has renders as an update card on
  // that row instead of a duplicate add card.
  const [actRows, setActRows] = useState([]);
  const [algRows, setAlgRows] = useState([]);
  const [fhRows, setFhRows] = useState([]);

  // Anthropometric — grouped-parameter resource. No manual add card
  // affordance in the transcript modal (the standalone rail's accordion
  // handles that). Cards on the right come from AI proposals + review
  // re-edit. Reviewers who want to add a fresh measurement manually
  // still do it through the embedded Anthropometric component's
  // param-name click (ANTH_V2_SELECT_PARAM), which we listen for and
  // materialise as an update card.
  const [manualCardsAnth, setManualCardsAnth] = useState([]);
  const [reeditCardsAnth, setReeditCardsAnth] = useState([]);

  // Daily schedule — timeline entries. Wave C first-cut supports only
  // add-entry captures; hydration + sleep captures are not wired yet.
  // No `+ Add timeline entry` shortcut here — the embedded DailySchedule
  // component keeps its own affordance (EntryModal opens over the
  // transcript modal via the shared portal), which handles manual entry
  // without competing with this rail. If we want a rail-side manual
  // card later, drop a listener for the DS event and mirror the
  // pattern from Activity.
  const [reeditCardsDs, setReeditCardsDs] = useState([]);

  // Live sheet count for the drug-schedule tab — DrugScheduleEditor
  // emits DS_V2_SHEET_COUNT with {count} whenever its stack length
  // changes. Drives BOTH the tab's "N open" pill (via `bump` below)
  // and the rail visibility gate (`dsSheetOpen = count > 0`), so we
  // no longer have to reconcile a boolean derived from separate
  // "open" and "close" events.
  const [dsSheetCount, setDsSheetCount] = useState(0);
  useEffect(() => {
    const onCount = (payload) => setDsSheetCount((payload && payload.count) || 0);
    EventService.addListener(DS_V2_SHEET_COUNT, onCount);
    return () => EventService.removeListener(DS_V2_SHEET_COUNT, onCount);
  }, []);
  const dsSheetOpen = dsSheetCount > 0;

  // Payload-change watcher. When the agent merges a follow-up AI mention
  // into an already-decided capture (state=added_in_call/edited_in_call),
  // its payload changes on the server but its state stays put — so the
  // proposals filter no longer surfaces it as a card, but the reviewer
  // has never seen the new fields. This effect notices those
  // in-place payload updates and materialises a reedit card so the
  // reviewer can (a) inspect the merged fields, (b) Save & next to
  // persist them, or (c) Cancel to drop them. Cancel just closes the
  // card; the peach row's overlay tracks `payload` today so the "old
  // values until save" contract isn't fully honoured until we also
  // snapshot a committed payload — separate follow-up.
  // Two-part tracking:
  //   prevPayloadRef        — last-seen payload object per captured item.
  //                             Used to compute the merged payload
  //                             (previous save-state + new AI fields)
  //                             when the reopen effect fires.
  //   seenReopenRef         — set of `${itemId}:${sig}` stamps already
  //                             materialised as reedit cards, so a
  //                             single agent merge doesn't spawn N cards.
  //                             Save handlers stamp their committed sig
  //                             into this set so a user-triggered
  //                             payload change (decideCaptured with
  //                             their edits) is NOT treated as a
  //                             reason to reopen.
  const prevPayloadRef = useRef(new Map());
  const seenReopenRef  = useRef(new Set());
  // Small helper the save handlers call so a user-committed payload
  // doesn't ricochet into an auto-reopen. Kept in a ref-stable form so
  // save handlers don't need to be listed in effect deps.
  const markSaveCommitted = useRef((itemId, payload) => {
    if (!itemId) return;
    const sig = JSON.stringify(payload || {});
    seenReopenRef.current.add(`${itemId}:${sig}`);
    // Advance the prev payload snapshot too — the item's payload IS
    // this committed value now, so the next render sees no delta.
    prevPayloadRef.current.set(itemId, payload || {});
  }).current;
  useEffect(() => {
    for (const it of items || []) {
      if (!it || !it.id) continue;
      const sig = JSON.stringify(it.payload || {});
      const prevPayload = prevPayloadRef.current.get(it.id);
      const prevSig = prevPayload !== undefined
        ? JSON.stringify(prevPayload)
        : undefined;
      prevPayloadRef.current.set(it.id, it.payload || {});
      if (prevSig === undefined) continue;            // first sight
      if (prevSig === sig) continue;                  // no change
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      // Guard against re-opening the same card repeatedly while the
      // agent keeps merging (each merge triggers a new signature).
      const stamp = `${it.id}:${sig}`;
      if (seenReopenRef.current.has(stamp)) continue;
      seenReopenRef.current.add(stamp);
      // Merge the last-seen (user-committed) payload with the current
      // (agent-updated) one so the reopened card lands with
      // user-saved fields preserved and only the newly-mentioned
      // fields overwritten. Same shape passed as initialPayload.
      const mergedInitialPayload = { ...(prevPayload || {}), ...(it.payload || {}) };
      switch (it.targetSection) {
        case "activity": {
          const record = it.operation === "update" && it.targetEntryId
            ? (actRows || []).find((r) => String(r.activityId) === String(it.targetEntryId))
            : null;
          setReeditCardsAct((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-act-${it.id}-${Date.now()}`, captured: it, actv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "allergies": {
          const record = it.operation === "update" && it.targetEntryId
            ? (algRows || []).find((r) => String(r.allergyId) === String(it.targetEntryId))
            : null;
          setReeditCardsAlg((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-alg-${it.id}-${Date.now()}`, captured: it, algv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "family_history": {
          const record = it.operation === "update" && it.targetEntryId
            ? (fhRows || []).find((r) => String(r.relativeId) === String(it.targetEntryId))
            : null;
          setReeditCardsFh((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-fh-${it.id}-${Date.now()}`, captured: it, famv2Record: record, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "hospitalisation": {
          setReeditCardsHosp((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-hosp-${it.id}-${Date.now()}`, captured: it, hospv2Record: null, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "medicines": {
          setReeditCardsMed((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-med-${it.id}-${Date.now()}`, captured: it, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "chief_complaints": {
          setReeditCards((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-cc-${it.id}-${Date.now()}`, captured: it, ccv2Record: null, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        case "diagnosis": {
          setReeditCardsDx((prev2) => (
            prev2.some((r) => r.captured.id === it.id)
              ? prev2
              : [{ cardId: `r-dx-${it.id}-${Date.now()}`, captured: it, dxv2Record: null, initialPayloadOverride: mergedInitialPayload }, ...prev2]
          ));
          break;
        }
        default:
          break;
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [items]);

  // Food preferences — whole-doc PUT resource; AI captures flow
  // straight into the middle-column FoodPreference form via
  // FP_V2_AI_INJECT (see effect below). No right-rail card and no
  // re-edit state on this side any more.

  const [saving, setSaving] = useState({}); // cardId → bool
  const [errors, setErrors] = useState({}); // cardId → string

  // Per-section total of OPEN cards = AI proposals still awaiting a
  // decision + manually-opened editors (manual add / update / re-edit).
  // Bubbled up to the modal via onOpenCountsChange so the modal-level
  // SectionTabs strip shows the "● Label N" pill and the Review call
  // button can gate on "all counts zero". Food preferences intentionally
  // returns 0 here — that section is inline-edited via the middle-column
  // form (no rail-side card), so any AI proposals for it merge into the
  // form's own dirty state rather than counting as an open card.
  const openCountsBySection = useMemo(() => {
    const acc = {};
    (items || []).forEach((it) => {
      if (!it || it.state !== "proposed") return;
      if (!it.targetSection) return;
      if (it.targetSection === "food_preferences") return;
      acc[it.targetSection] = (acc[it.targetSection] || 0) + 1;
    });
    const bump = (section, n) => {
      if (!n) return;
      acc[section] = (acc[section] || 0) + n;
    };
    bump("chief_complaints",   manualCardIds.length    + updateCards.length    + reeditCards.length);
    bump("diagnosis",          manualCardIdsDx.length  + updateCardsDx.length  + reeditCardsDx.length);
    bump("medicines",          manualCardIdsMed.length + reeditCardsMed.length + updateCardsMed.length);
    bump("hospitalisation",    manualCardIdsHosp.length + reeditCardsHosp.length + updateCardsHosp.length);
    bump("lab_investigations", manualCardIdsLab.length + reeditCardsLab.length);
    bump("allergies",          manualCardIdsAlg.length + reeditCardsAlg.length + updateCardsAlg.length);
    bump("family_history",     manualCardIdsFh.length  + reeditCardsFh.length  + updateCardsFh.length);
    bump("addictions",         updateCardsAdd.length   + reeditCardsAdd.length);
    bump("activity",           manualCardIdsAct.length + reeditCardsAct.length + updateCardsAct.length);
    bump("anthropometric",     manualCardsAnth.length  + reeditCardsAnth.length);
    bump("daily_schedule",     reeditCardsDs.length);
    // drug_schedule counts the DrugScheduleEditor's live sheet stack —
    // matches CC/Dx where an in-flight manual/re-edit card contributes
    // a per-card "N" pill until each is saved or cancelled.
    bump("drug_schedule",      dsSheetCount);
    // food_preferences intentionally omitted (see comment above).
    return acc;
  }, [
    items,
    manualCardIds, updateCards, reeditCards,
    manualCardIdsDx, updateCardsDx, reeditCardsDx,
    manualCardIdsMed, reeditCardsMed, updateCardsMed,
    manualCardIdsHosp, reeditCardsHosp, updateCardsHosp,
    manualCardIdsLab, reeditCardsLab,
    manualCardIdsAlg, reeditCardsAlg, updateCardsAlg,
    manualCardIdsFh, reeditCardsFh, updateCardsFh,
    updateCardsAdd, reeditCardsAdd,
    manualCardIdsAct, reeditCardsAct, updateCardsAct,
    manualCardsAnth, reeditCardsAnth,
    reeditCardsDs,
    dsSheetCount,
  ]);

  // Fire the change callback whenever the counts shape changes. Guarded
  // via a ref so re-renders that produce equivalent counts don't cause
  // parent state churn (which would cascade back into props and loop).
  const lastOpenCountsRef = useRef("");
  useEffect(() => {
    const serialized = JSON.stringify(openCountsBySection);
    if (serialized === lastOpenCountsRef.current) return;
    lastOpenCountsRef.current = serialized;
    if (typeof onOpenCountsChange === "function") {
      onOpenCountsChange(openCountsBySection);
    }
  }, [openCountsBySection, onOpenCountsChange]);

  // Food-preferences special-case: no right-rail card is rendered for
  // this section (unlike every other capture). Instead, each new AI
  // food-preference proposal is forwarded to the middle-column
  // FoodPreference form via an event bus so it merges into the form's
  // local Likes/Dislikes/Restrictions/Limitations chip list — the
  // reviewer sees the AI's suggestion appear inline and hits the
  // form's own Save button when ready. Payload carries the item id
  // so the FoodPreference form can dedup by id and ignore re-emits
  // (fires on every items[] change and also on food-preferences tab
  // activation, so a late-mounting form catches proposals that landed
  // before it appeared).
  const emitFpInject = (it) => {
    EventService.emit(FP_V2_AI_INJECT, { id: it.id, ...(it.payload || {}) });
  };
  useEffect(() => {
    (items || []).forEach((it) => {
      if (!it || it.state !== "proposed") return;
      if (it.targetSection !== "food_preferences") return;
      emitFpInject(it);
    });
  }, [items]);
  useEffect(() => {
    if (activeSection !== "food_preferences") return;
    (items || []).forEach((it) => {
      if (!it || it.state !== "proposed") return;
      if (it.targetSection !== "food_preferences") return;
      emitFpInject(it);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Past-transcript (viewMode="review") click-to-open card. Reviewer
  // clicks a row's status chip in the SectionCaptureTable → we open
  // the same S5 card next to the table, populated from the captured
  // payload. "Save" in this card is view-only — the transcript is
  // already saved, so onSave just closes. Only one card at a time;
  // second click swaps.
  const [openedReviewId, setOpenedReviewId] = useState("");
  // Reset when the reviewer switches to another section so a stale
  // captured id from a different section doesn't try to render
  // through the wrong per-section form branch.
  useEffect(() => {
    setOpenedReviewId("");
  }, [activeSection]);

  // Listen for CC_V2_ADD + CC_V2_EDIT (fired from the embedded
  // ChiefComplaints's "+ Add complaints" toolbar and its per-row status
  // chip). Only intercepts when the Chief Complaints tab is active in
  // the modal; otherwise the sidebar's editor rail handles them.
  useEffect(() => {
    if (activeSection !== "chief_complaints") return undefined;
    const openManual = () => {
      setManualCardIds((prev) => {
        // Blank-card guard — matches ChiefComplaintEditor's sheet-stack
        // behaviour on the standalone page.
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-${Date.now()}`), ...prev];
      });
    };
    const openUpdate = (payload) => {
      const c = payload && payload.complaint;
      if (!c || !c.complaintId) return;
      setUpdateCards((prev) => {
        // Don't stack two update sheets for the same complaint — first
        // wins, matches ComplaintForm's sheet-stack dup guard.
        if (prev.some((u) => u.complaint.complaintId === c.complaintId)) return prev;
        return [{ cardId: withPendingFocus(`u-${c.complaintId}-${Date.now()}`), complaint: c }, ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      setReeditCards((prev) => {
        // One re-edit sheet per underlying captured item — clicking the
        // chip twice re-focuses the existing sheet rather than stacking.
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [
          { cardId: `r-${captured.id}`, captured, ccv2Record: (payload && payload.ccv2Record) || null },
          ...prev,
        ];
      });
    };
    EventService.addListener(CC_V2_ADD, openManual);
    EventService.addListener(CC_V2_EDIT, openUpdate);
    EventService.addListener(CC_V2_EDIT_CAPTURED, openReedit);
    return () => {
      EventService.removeListener(CC_V2_ADD, openManual);
      EventService.removeListener(CC_V2_EDIT, openUpdate);
      EventService.removeListener(CC_V2_EDIT_CAPTURED, openReedit);
    };
  }, [activeSection]);

  // Same pattern for the Diagnosis tab. Only intercepts when Diagnosis
  // is the active tab; standalone sidebar's DiagnosisEditor handles
  // DX_V2_ADD / DX_V2_STATUS the rest of the time.
  useEffect(() => {
    if (activeSection !== "diagnosis") return undefined;
    const openManual = () => {
      setManualCardIdsDx((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-dx-${Date.now()}`), ...prev];
      });
    };
    const openUpdate = (payload) => {
      const d = payload && payload.diagnosis;
      if (!d || !d.diagnosisId) return;
      setUpdateCardsDx((prev) => {
        if (prev.some((u) => u.diagnosis.diagnosisId === d.diagnosisId)) return prev;
        return [{ cardId: withPendingFocus(`u-dx-${d.diagnosisId}-${Date.now()}`), diagnosis: d }, ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      setReeditCardsDx((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [
          {
            cardId:     `r-dx-${captured.id}`,
            captured,
            dxv2Record: (payload && payload.dxv2Record) || null,
          },
          ...prev,
        ];
      });
    };
    EventService.addListener(DX_V2_ADD, openManual);
    EventService.addListener(DX_V2_STATUS, openUpdate);
    EventService.addListener(DX_V2_EDIT_CAPTURED, openReedit);
    return () => {
      EventService.removeListener(DX_V2_ADD, openManual);
      EventService.removeListener(DX_V2_STATUS, openUpdate);
      EventService.removeListener(DX_V2_EDIT_CAPTURED, openReedit);
    };
  }, [activeSection]);

  // Medicines tab — only listens for MED_V2_ADD (manual "+ Add medicine"
  // toolbar). MED_V2_STATUS / MED_V2_ADHERENCE / re-edit-captured events
  // are Wave A pass 2 (need MedicineStatusForm extension). Re-edit for
  // staged-add is opened via ChiefComplaints-style openUpdate branching
  // on the standalone Medicines component; that landing pad is deferred
  // until we add MED_V2_EDIT_CAPTURED to the events module.
  useEffect(() => {
    if (activeSection !== "medicines") return undefined;
    const openManual = () => {
      setManualCardIdsMed((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-med-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      setReeditCardsMed((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-med-${captured.id}`, captured }, ...prev];
      });
    };
    // Medicine name / STATUS chip → open an update card that renders
    // MedicineStatusForm for that record. Dedup by medicineId so a
    // repeat click surfaces the existing card instead of stacking.
    const openUpdate = (payload) => {
      const m = payload && payload.medicine;
      if (!m || !m.medicineId) return;
      setUpdateCardsMed((prev) => {
        if (prev.some((u) => u.medicine.medicineId === m.medicineId)) return prev;
        // MedicineStatusForm requires a numeric formId — Date.now()
        // ticks up monotonically so it doubles as the React key and
        // the form's DOM-id suffix without needing any conversion.
        return [{ cardId: withPendingFocus(Date.now()), medicine: m, mode: payload.mode || "status" }, ...prev];
      });
    };
    EventService.addListener(MED_V2_ADD, openManual);
    EventService.addListener(MED_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(MED_V2_STATUS, openUpdate);
    return () => {
      EventService.removeListener(MED_V2_ADD, openManual);
      EventService.removeListener(MED_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(MED_V2_STATUS, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "hospitalisation") return undefined;
    const openManual = () => {
      setManualCardIdsHosp((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-hosp-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const hospv2Record = payload && payload.hospv2Record;
      setReeditCardsHosp((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-hosp-${captured.id}`, captured, hospv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const h = payload && payload.hospitalisation;
      if (!h || !h.hospitalisationId) return;
      setUpdateCardsHosp((prev) => {
        if (prev.some((u) => u.record.hospitalisationId === h.hospitalisationId)) return prev;
        return [{ cardId: withPendingFocus(`u-hosp-${h.hospitalisationId}-${Date.now()}`), record: h }, ...prev];
      });
    };
    EventService.addListener(HOSP_V2_ADD, openManual);
    EventService.addListener(HOSP_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(HOSP_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(HOSP_V2_ADD, openManual);
      EventService.removeListener(HOSP_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(HOSP_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "lab_investigations") return undefined;
    // Lab uses one channel, LI_V2_EDIT, but the payload shape decides
    // what opens:
    //   • {captured} — the transcript LI table clicked an in-call
    //     captured row → open the row as a reedit DigitiseManuallyCard
    //     seeded from its payload.
    //   • {investigation} — legacy standalone row-click for the LI
    //     v2 editor rail, not the transcript flow. Still ignored here.
    //   • blank / {} — "+ Add investigation" or the empty-state CTA →
    //     push a new blank manual card into the rail.
    const openManual = (payload) => {
      if (payload && payload.captured) {
        const captured = payload.captured;
        const capturedId = captured.id || "";
        setReeditCardsLab((prev) => {
          // Dedup — clicking the same peach row twice re-focuses the
          // existing sheet instead of stacking a duplicate.
          if (prev.some((r) => r.captured && r.captured.id === capturedId)) return prev;
          return [{ cardId: withPendingFocus(`r-lab-${capturedId || Date.now()}`), captured }, ...prev];
        });
        return;
      }
      if (payload && payload.investigation) return; // legacy standalone row-click
      // Append (not prepend) so a new empty card lands BELOW the
      // one the reviewer just clicked "+ Add another test" from —
      // matches the "queue a sibling below" mental model. Blank-guard
      // uses the same cardFilledRef every other section uses; the card
      // now emits onFilledChange when it gains / loses a parent lab
      // test pick.
      setManualCardIdsLab((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [...prev, withPendingFocus(`m-lab-${Date.now()}`)];
      });
    };
    EventService.addListener(LI_V2_EDIT, openManual);
    return () => {
      EventService.removeListener(LI_V2_EDIT, openManual);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "allergies") return undefined;
    const openManual = () => {
      setManualCardIdsAlg((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-alg-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const algv2Record = payload && payload.algv2Record;
      setReeditCardsAlg((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-alg-${captured.id}`, captured, algv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const a = payload && payload.allergy;
      if (!a || !a.allergyId) return;
      setUpdateCardsAlg((prev) => {
        if (prev.some((u) => u.record.allergyId === a.allergyId)) return prev;
        return [{ cardId: withPendingFocus(`u-alg-${a.allergyId}-${Date.now()}`), record: a }, ...prev];
      });
    };
    EventService.addListener(ALG_V2_ADD, openManual);
    EventService.addListener(ALG_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(ALG_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(ALG_V2_ADD, openManual);
      EventService.removeListener(ALG_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(ALG_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "family_history") return undefined;
    const openManual = () => {
      setManualCardIdsFh((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-fh-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const famv2Record = payload && payload.famv2Record;
      setReeditCardsFh((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-fh-${captured.id}`, captured, famv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const r = payload && payload.relative;
      if (!r || !r.relativeId) return;
      setUpdateCardsFh((prev) => {
        if (prev.some((u) => u.record.relativeId === r.relativeId)) return prev;
        return [{ cardId: withPendingFocus(`u-fh-${r.relativeId}-${Date.now()}`), record: r }, ...prev];
      });
    };
    EventService.addListener(FAM_V2_ADD, openManual);
    EventService.addListener(FAM_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(FAM_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(FAM_V2_ADD, openManual);
      EventService.removeListener(FAM_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(FAM_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "addictions") return undefined;
    // The embedded Addictions component emits ADD_V2_EDIT with the row
    // the user clicked. Fixed 4-slot resource — no "add manually" event
    // — every card starts as an update of a real row. Dedup on
    // addiction.key so a second click on the same row doesn't stack a
    // second card.
    const openUpdate = (payload) => {
      const a = payload && payload.addiction;
      if (!a || !a.key) return;
      setUpdateCardsAdd((prev) => {
        if (prev.some((c) => c.addiction && c.addiction.key === a.key)) return prev;
        return [{ cardId: withPendingFocus(`u-add-${a.key}-${Date.now()}`), addiction: a }, ...prev];
      });
    };
    EventService.addListener(ADD_V2_EDIT, openUpdate);
    return () => {
      EventService.removeListener(ADD_V2_EDIT, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "activity") return undefined;
    const openManual = () => {
      setManualCardIdsAct((prev) => {
        const blankId = findBlankCardId(prev);
        if (blankId) { refuseAsBlank(blankId); return prev; }
        return [withPendingFocus(`m-act-${Date.now()}`), ...prev];
      });
    };
    const openReedit = (payload) => {
      const captured = payload && payload.captured;
      if (!captured || !captured.id) return;
      const actv2Record = payload && payload.actv2Record;
      setReeditCardsAct((prev) => {
        if (prev.some((r) => r.captured.id === captured.id)) return prev;
        return [{ cardId: `r-act-${captured.id}`, captured, actv2Record }, ...prev];
      });
    };
    const openUpdate = (payload) => {
      const a = payload && payload.activity;
      if (!a || !a.activityId) return;
      setUpdateCardsAct((prev) => {
        if (prev.some((u) => u.record.activityId === a.activityId)) return prev;
        return [{ cardId: withPendingFocus(`u-act-${a.activityId}-${Date.now()}`), record: a }, ...prev];
      });
    };
    EventService.addListener(ACT_V2_ADD, openManual);
    EventService.addListener(ACT_V2_EDIT_CAPTURED, openReedit);
    EventService.addListener(ACT_V2_EDIT, openUpdate);
    EventService.addListener(ACT_V2_STATUS, openUpdate);
    return () => {
      EventService.removeListener(ACT_V2_ADD, openManual);
      EventService.removeListener(ACT_V2_EDIT_CAPTURED, openReedit);
      EventService.removeListener(ACT_V2_EDIT, openUpdate);
      EventService.removeListener(ACT_V2_STATUS, openUpdate);
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "anthropometric") return undefined;
    // The embedded Anthropometric component emits
    // ANTH_V2_SELECT_PARAM with a parameter key when the user clicks a
    // row's name. Materialise as a manual update card for that
    // parameter, deduping so a repeat click doesn't stack cards.
    const openManual = (key) => {
      if (typeof key !== "string" || !key) return;
      // BMI + WHR are server-derived — reject clicks on them if any.
      if (key === "bmi" || key === "whr") return;
      setManualCardsAnth((prev) => {
        if (prev.some((c) => c.parameter === key)) return prev;
        return [{ cardId: `m-anth-${key}-${Date.now()}`, parameter: key }, ...prev];
      });
    };
    EventService.addListener(ANTH_V2_SELECT_PARAM, openManual);
    return () => {
      EventService.removeListener(ANTH_V2_SELECT_PARAM, openManual);
    };
  }, [activeSection]);

  const applyMutation = (resp) => {
    if (!resp || !resp.transcript) return;
    if (Array.isArray(resp.transcript.captured) && onItemsSync) {
      onItemsSync(resp.transcript.captured);
    }
    if (typeof resp.transcript.version === "number" && onVersionSync) {
      onVersionSync(resp.transcript.version);
    }
  };

  // AI card Save — updates the captured item's payload + moves it to
  // edited_in_call state. Server rejects invalid transitions (e.g. if
  // the item was already discarded); we surface that error inline.
  const saveAiCard = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  // Manual card Save — creates a fresh captured item (state=proposed via
  // POST /captured) then immediately moves it to added_in_call. Two API
  // calls but reuses existing endpoints; confidence=0 is our "manual
  // source" heuristic until we add a proper Source field on the model.
  const saveManualCard = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "chief_complaints",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        // Find the freshly-created item so we can advance its state.
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "chief_complaints" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIds((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIds((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCard = (cardId) => {
    setManualCardIds((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // Update card Save — stashes an "update" captured item on the
  // transcript. The review flow (Phase 4) picks these up + calls CC v2's
  // updateStatus on the referenced complaintId. In-call the item lives
  // only in the transcript buffer; the standalone ChiefComplaints table
  // still shows the untouched record.
  const saveUpdateCard = (cardId, complaint, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    // uniqueKey deliberately omits cardId — every "Update this complaint"
    // Save collapses onto the same captured item via Go's propose merge-
    // by-uniqueKey, so repeat clicks don't stack duplicate captures.
    const uniqueKey = `update:${complaint.complaintId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "chief_complaints",
        operation:     "update",
        targetEntryId: String(complaint.complaintId),
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "chief_complaints" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setUpdateCards((prev) => prev.filter((u) => u.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setUpdateCards((prev) => prev.filter((u) => u.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelUpdateCard = (cardId) => {
    setUpdateCards((prev) => prev.filter((u) => u.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // Shared propose+decide flow for update-card saves across every
  // section that materialises an update card off a middle-column row
  // click (Hosp / Alg / FH / PA). Same shape as CC's saveUpdateCard but
  // parameterised by section id, targetEntryId, and the setter for the
  // section's own updateCards state. Uses `update:<id>` as uniqueKey so
  // repeat clicks on the same row collapse onto one captured item on
  // the server side.
  const saveGenericUpdateCard = (
    cardId, targetSection, targetEntryId, payload, removeFromUpdateCards,
  ) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `update:${targetEntryId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection,
        operation:      "update",
        targetEntryId:  String(targetEntryId),
        uniqueKey,
        payload,
        confidence:     0,
        evidence:       "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === targetSection && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          removeFromUpdateCards(cardId);
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            removeFromUpdateCards(cardId);
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const removeUpdateHosp = (cardId) =>
    setUpdateCardsHosp((prev) => prev.filter((u) => u.cardId !== cardId));
  const removeUpdateAlg = (cardId) =>
    setUpdateCardsAlg((prev) => prev.filter((u) => u.cardId !== cardId));
  const removeUpdateFh = (cardId) =>
    setUpdateCardsFh((prev) => prev.filter((u) => u.cardId !== cardId));
  const removeUpdateAct = (cardId) =>
    setUpdateCardsAct((prev) => prev.filter((u) => u.cardId !== cardId));

  const saveUpdateCardHosp = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "hospitalisation", record.hospitalisationId, payload, removeUpdateHosp);
  const saveUpdateCardAlg  = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "allergies",       record.allergyId,          payload, removeUpdateAlg);
  const saveUpdateCardFh   = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "family_history",  record.relativeId,         payload, removeUpdateFh);
  const saveUpdateCardAct  = (cardId, record, payload) =>
    saveGenericUpdateCard(cardId, "activity",        record.activityId,         payload, removeUpdateAct);

  const cancelUpdateCardHosp = (cardId) => { removeUpdateHosp(cardId); setErrors((p) => ({ ...p, [cardId]: "" })); };
  const cancelUpdateCardAlg  = (cardId) => { removeUpdateAlg(cardId);  setErrors((p) => ({ ...p, [cardId]: "" })); };
  const cancelUpdateCardFh   = (cardId) => { removeUpdateFh(cardId);   setErrors((p) => ({ ...p, [cardId]: "" })); };
  const cancelUpdateCardAct  = (cardId) => { removeUpdateAct(cardId);  setErrors((p) => ({ ...p, [cardId]: "" })); };

  // Re-edit save — updates the SAME captured item's payload via
  // decideCaptured. Works for both operation="add" (staged-add rows
  // being tweaked) and operation="update" (staged-update rows being
  // tweaked). The state transition is edited_in_call → edited_in_call
  // when already edited, or added_in_call → edited_in_call on first
  // re-edit — both allowed by the server's state machine.
  const saveReeditCard = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCards((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCard = (cardId) => {
    setReeditCards((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Diagnosis save handlers — mirror the CC ones exactly, differing
  // only in the section slug on propose + which state array to clean.
  // Later refactor: one generic set of handlers taking `section` as arg.
  const saveAiCardDx = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardDx = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "diagnosis",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "diagnosis" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsDx((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsDx((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardDx = (cardId) => {
    setManualCardIdsDx((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveUpdateCardDx = (cardId, diagnosis, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `update:${diagnosis.diagnosisId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "diagnosis",
        operation:     "update",
        targetEntryId: String(diagnosis.diagnosisId),
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "diagnosis" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setUpdateCardsDx((prev) => prev.filter((u) => u.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setUpdateCardsDx((prev) => prev.filter((u) => u.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelUpdateCardDx = (cardId) => {
    setUpdateCardsDx((prev) => prev.filter((u) => u.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardDx = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsDx((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardDx = (cardId) => {
    setReeditCardsDx((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Medicines save handlers — Wave A first-cut adds only. Mirror
  // of the CC/Dx handlers with `targetSection: "medicines"`.
  const saveAiCardMed = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardMed = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "medicines",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "medicines" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsMed((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsMed((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardMed = (cardId) => {
    setManualCardIdsMed((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardMed = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsMed((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardMed = (cardId) => {
    setReeditCardsMed((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Hospitalisation save handlers — Wave A first-cut adds only.
  const saveAiCardHosp = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardHosp = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "hospitalisation",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "hospitalisation" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsHosp((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsHosp((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardHosp = (cardId) => {
    setManualCardIdsHosp((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardHosp = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsHosp((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardHosp = (cardId) => {
    setReeditCardsHosp((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Lab investigations save handlers — Wave A first-cut adds only.
  const saveAiCardLab = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardLab = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "lab_investigations",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "lab_investigations" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsLab((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsLab((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardLab = (cardId) => {
    setManualCardIdsLab((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardLab = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsLab((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardLab = (cardId) => {
    setReeditCardsLab((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Allergies save handlers — Wave B first-cut adds only.
  const saveAiCardAlg = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardAlg = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "allergies",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "allergies" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsAlg((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsAlg((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardAlg = (cardId) => {
    setManualCardIdsAlg((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAlg = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAlg((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAlg = (cardId) => {
    setReeditCardsAlg((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Family history save handlers — Wave B first-cut adds only.
  const saveAiCardFh = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardFh = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "family_history",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "family_history" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsFh((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsFh((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardFh = (cardId) => {
    setManualCardIdsFh((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardFh = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsFh((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardFh = (cardId) => {
    setReeditCardsFh((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Addictions save handlers — Wave B first-cut. Every save is an
  //     "add" op on the transcript (backend rewrites the slot on every
  //     write; there's no distinct update path for the fixed 4-slot
  //     addictions resource).
  const saveAiCardAdd = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  // Update-flow save (from the row-name / chip-click card). Same wire
  // as saveManualCardFh but tagged section=addictions + dedup key
  // baked from the addiction.key so a second click on the same row
  // reuses the same captured item.
  const saveUpdateCardAdd = (cardId, addiction, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `addiction:${addiction.key}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "addictions",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "addictions" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setUpdateCardsAdd((prev) => prev.filter((c) => c.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setUpdateCardsAdd((prev) => prev.filter((c) => c.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelUpdateCardAdd = (cardId) => {
    setUpdateCardsAdd((prev) => prev.filter((c) => c.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAdd = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAdd((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAdd = (cardId) => {
    setReeditCardsAdd((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Physical activity save handlers — Wave B first-cut adds only.
  const saveAiCardAct = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    markSaveCommitted(item.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardAct = (cardId, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `manual:${cardId}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "activity",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "activity" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardIdsAct((prev) => prev.filter((id) => id !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardIdsAct((prev) => prev.filter((id) => id !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardAct = (cardId) => {
    setManualCardIdsAct((prev) => prev.filter((id) => id !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAct = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    markSaveCommitted(captured.id, payload);
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAct((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAct = (cardId) => {
    setReeditCardsAct((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Anthropometric save handlers — Wave C first-cut. Every save is
  //     an "add" op on the transcript (backend derives Add vs
  //     Correction based on prior history; we just POST to the same
  //     endpoint on either path).
  const saveAiCardAnth = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveManualCardAnth = (cardId, parameter, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    const uniqueKey = `anthropometric:${parameter}`;
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection: "anthropometric",
        operation:     "add",
        uniqueKey,
        payload,
        confidence:    0,
        evidence:      "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "anthropometric" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          setSaving((prev) => ({ ...prev, [cardId]: false }));
          setManualCardsAnth((prev) => prev.filter((c) => c.cardId !== cardId));
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setManualCardsAnth((prev) => prev.filter((c) => c.cardId !== cardId));
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [cardId]: false }));
            setErrors((prev) => ({
              ...prev,
              [cardId]: (err && err.message) || "Could not finalise. Try again.",
            }));
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const cancelManualCardAnth = (cardId) => {
    setManualCardsAnth((prev) => prev.filter((c) => c.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  const saveReeditCardAnth = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsAnth((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardAnth = (cardId) => {
    setReeditCardsAnth((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // ─── Daily schedule save handlers — Wave C first-cut adds only.
  const saveAiCardDs = (item, payload) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };

  const saveReeditCardDs = (cardId, captured, payload) => {
    setSaving((prev) => ({ ...prev, [cardId]: true }));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setReeditCardsDs((prev) => prev.filter((r) => r.cardId !== cardId));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [cardId]: false }));
        setErrors((prev) => ({
          ...prev,
          [cardId]: (err && err.message) || "Could not save. Try again.",
        }));
      },
    );
  };
  const cancelReeditCardDs = (cardId) => {
    setReeditCardsDs((prev) => prev.filter((r) => r.cardId !== cardId));
    setErrors((prev) => ({ ...prev, [cardId]: "" }));
  };

  // Drug schedule manual add — the middle-column DrugSchedule's
  // "+ Add schedule" fires DS_V2_ADD, useSheetStack in DrugScheduleEditor
  // pushes a sheet, the sheet calls this handler on Save. We do the
  // propose+decide dance so the plan lands in /captured with
  // state=added_in_call — Review Save & next then fans it out via the
  // transcript service's writeDrugSchedule (which owns the pharmacy
  // POST). No manualCardIds tracking here because the sheet lifecycle
  // is entirely inside useSheetStack; success/error come back via
  // the callback pair the sheet passes in.
  const saveManualCardDrug = (body, cbs) => {
    const uniqueKey = `manual:drug-${Date.now()}`;
    const payload = drugScheduleBodyToCapturedAdd(body);
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection:   "drug_schedule",
        operation:       "add",
        uniqueKey,
        payload,
        confidence:      0,
        evidence:        "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "drug_schedule" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          if (cbs && cbs.onSuccess) cbs.onSuccess();
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState: "added_in_call", expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            if (cbs && cbs.onSuccess) cbs.onSuccess();
          },
          (err) => { if (cbs && cbs.onError) cbs.onError(err); },
        );
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // Drug schedule save — matches CC's saveAiCard: always
  // edited_in_call, regardless of whether the source was a `proposed`
  // AI capture the reviewer is accepting or an `added_in_call` staged
  // plan being re-edited. The Go state machine accepts both
  // transitions, and using one state keeps the rest of the pipeline
  // (Review & Save filter, tab counters) consistent across sections.
  const updateManualCardDrug = (capturedId, body, cbs) => {
    const payload = drugScheduleBodyToCapturedAdd(body);
    transcriptService.decideCaptured(
      patientId, transcriptId, capturedId,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        if (cbs && cbs.onSuccess) cbs.onSuccess();
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // Drug schedule AI proposals — each new `proposed` capture auto-opens
  // an is-ai themed AddSheet in the DrugScheduleEditor rail (via
  // DS_V2_EDIT_CAPTURED with a plan-shape wrapping the payload). The
  // reviewer edits fields as needed and clicks Add; updateManualCardDrug
  // above transitions the state to added_in_call, at which point the
  // item appears in the middle-column plan table as a peach row.
  //
  // Track already-auto-opened proposal ids in a ref so a re-render or
  // an unrelated `items` update doesn't spawn a duplicate sheet. Ids
  // are never removed — once opened, always considered handled. If the
  // reviewer cancels the sheet without clicking Add, the proposal
  // stays in state=proposed and surfaces on Review & save.
  const openedDrugProposalsRef = useRef(new Set());
  useEffect(() => {
    // DrugScheduleEditor is only mounted while drug_schedule is the
    // active tab (CapturedSections gates the right-column render).
    // Firing DS_V2_EDIT_CAPTURED when the editor isn't listening would
    // silently drop the sheet, so we gate on the same condition and
    // let this effect re-run on tab activation — any proposals that
    // arrived while another tab was active auto-open on switch back.
    if (activeSection !== "drug_schedule") return;
    if (!Array.isArray(items) || items.length === 0) return;
    for (const it of items) {
      if (!it || it.targetSection !== "drug_schedule") continue;
      if (it.state !== "proposed") continue;
      if (openedDrugProposalsRef.current.has(it.id)) continue;
      openedDrugProposalsRef.current.add(it.id);
      // dsStagedAddToPlan handles the "AI captured atoms but no
      // expanded doses[] yet" case by synthesising doses from
      // drug_name / start_date / num_doses / interval_value /
      // interval_unit — same math the Go adapter runs on commit.
      // Without this the AddSheet seed sees an empty doses[] and
      // can't populate the drug picker or dose plan.
      const plan = dsStagedAddToPlan(it);
      EventService.emit(DS_V2_EDIT_CAPTURED, { plan, patient });
    }
  }, [activeSection, items, patient]);

  // Patient details manual card save. Single-record UPDATE section:
  // one and only one captured item ever exists per transcript for
  // patient_details (uniqueKey is fixed at "patient_details:main" so
  // the Go proposeCaptured handler upserts onto the same row every
  // time — a subsequent manual save or AI capture merges into it,
  // never appends). No cardId tracking here because the rail is a
  // single-instance editor driven by PD_V2_EDIT + PD_V2_CLOSE, not
  // useSheetStack.
  //
  // State-transition subtlety unique to this section: because we upsert
  // onto the same row, the item's state after the FIRST save is
  // added_in_call. The Go state machine forbids added_in_call →
  // added_in_call, so subsequent saves must route to edited_in_call
  // instead (legal from both proposed and added_in_call). The rule:
  //   from `proposed`                          → added_in_call
  //   from `added_in_call` / `edited_in_call`  → edited_in_call
  const saveManualCardPatient = (body, cbs) => {
    const uniqueKey = "patient_details:main";
    const payload = patientDetailsBodyToCapturedUpdate(body);
    transcriptService.proposeCaptured(
      patientId, transcriptId,
      {
        targetSection:   "patient_details",
        operation:       "update",
        uniqueKey,
        payload,
        confidence:      0,
        evidence:        "",
        expectedVersion: version || 0,
      },
      (proposeResp) => {
        applyMutation(proposeResp);
        const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
        const created = (fresh || []).find(
          (it) => it.targetSection === "patient_details" && it.uniqueKey === uniqueKey,
        );
        if (!created) {
          if (cbs && cbs.onSuccess) cbs.onSuccess();
          return;
        }
        const nextVersion =
          (proposeResp.transcript && proposeResp.transcript.version) || version || 0;
        // Pick the transition based on the item's current state
        // (added_in_call → added_in_call is illegal on the Go side).
        const newState =
          created.state === "added_in_call" || created.state === "edited_in_call"
            ? "edited_in_call"
            : "added_in_call";
        transcriptService.decideCaptured(
          patientId, transcriptId, created.id,
          { newState, expectedVersion: nextVersion },
          (decideResp) => {
            applyMutation(decideResp);
            if (cbs && cbs.onSuccess) cbs.onSuccess();
          },
          (err) => { if (cbs && cbs.onError) cbs.onError(err); },
        );
      },
      (err) => { if (cbs && cbs.onError) cbs.onError(err); },
    );
  };

  // Food-preference save/re-edit handlers were removed with the
  // right-rail card — captures merge into the middle-column form via
  // FP_V2_AI_INJECT and persist through the form's own Save button.
  // Save-and-next at Review time still commits any pending proposals
  // via the existing SaveSection fan-out.

  // Cancel on an AI card = discard the AI proposal (mark discarded).
  // The item disappears from the right column and does NOT show up in
  // review.
  const discardAi = (item) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "discarded_in_call", expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSaving((prev) => ({ ...prev, [item.id]: false }));
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not discard.",
        }));
      },
    );
  };

  // AI-proposal dedup save: an AI proposal whose name matches an
  // existing patient row is rendered as an update card on that record
  // instead of a duplicate add. On save we (a) discard the original
  // ADD proposal so it doesn't linger, then (b) propose+decide a fresh
  // UPDATE captured item bound to the matched record. Same propose+
  // decide flow saveGenericUpdateCard already uses.
  const saveAiProposalAsUpdate = (
    item, targetSection, targetEntryId, payload, onSuccess, onFailure,
  ) => {
    setSaving((prev) => ({ ...prev, [item.id]: true }));
    setErrors((prev) => ({ ...prev, [item.id]: "" }));
    const uniqueKey = `update:${targetEntryId}`;
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "discarded_in_call", expectedVersion: version || 0 },
      (discardResp) => {
        applyMutation(discardResp);
        const nextV1 =
          (discardResp.transcript && discardResp.transcript.version) || version || 0;
        transcriptService.proposeCaptured(
          patientId, transcriptId,
          {
            targetSection,
            operation:      "update",
            targetEntryId:  String(targetEntryId),
            uniqueKey,
            payload,
            confidence:     item.confidence || 0,
            evidence:       item.evidence || "",
            expectedVersion: nextV1,
          },
          (proposeResp) => {
            applyMutation(proposeResp);
            const fresh = proposeResp && proposeResp.transcript && proposeResp.transcript.captured;
            const created = (fresh || []).find(
              (c) => c.targetSection === targetSection && c.uniqueKey === uniqueKey,
            );
            if (!created) {
              setSaving((prev) => ({ ...prev, [item.id]: false }));
              if (onSuccess) onSuccess();
              return;
            }
            const nextV2 =
              (proposeResp.transcript && proposeResp.transcript.version) || nextV1;
            transcriptService.decideCaptured(
              patientId, transcriptId, created.id,
              { newState: "added_in_call", expectedVersion: nextV2 },
              (decideResp) => {
                applyMutation(decideResp);
                setSaving((prev) => ({ ...prev, [item.id]: false }));
                if (onSuccess) onSuccess();
              },
              (err) => {
                setSaving((prev) => ({ ...prev, [item.id]: false }));
                setErrors((prev) => ({
                  ...prev,
                  [item.id]: (err && err.message) || "Could not finalise. Try again.",
                }));
                if (onFailure) onFailure(err);
              },
            );
          },
          (err) => {
            setSaving((prev) => ({ ...prev, [item.id]: false }));
            setErrors((prev) => ({
              ...prev,
              [item.id]: (err && err.message) || "Could not save. Try again.",
            }));
            if (onFailure) onFailure(err);
          },
        );
      },
      (err) => {
        setSaving((prev) => ({ ...prev, [item.id]: false }));
        setErrors((prev) => ({
          ...prev,
          [item.id]: (err && err.message) || "Could not discard. Try again.",
        }));
        if (onFailure) onFailure(err);
      },
    );
  };

  const forSection = activeSection
    ? items.filter((it) => it.targetSection === activeSection)
    : [];
  const addedRows  = forSection.filter(
    (it) => it.state === "added_in_call" || it.state === "edited_in_call",
  );
  const proposals  = forSection.filter((it) => it.state === "proposed");

  // Drug schedule — staged plans (added or edited during this call)
  // that DrugScheduleEditor's picker should also treat as "already on
  // an active schedule" so the reviewer can't add the same drug twice
  // via two staged rows. Server-loaded active plans are already
  // handled inside DrugScheduleEditor via its own list fetch.
  const stagedDrugSchedNames = useMemo(() => {
    const s = new Set();
    for (const it of items || []) {
      if (!it || it.targetSection !== "drug_schedule") continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      if ((it.operation || "add") !== "add") continue;
      const p = it.payload || {};
      const firstDose = Array.isArray(p.doses) && p.doses[0];
      const name = String(
        p.drug_name || (firstDose && firstDose.dosename) || "",
      ).trim().toLowerCase();
      if (name) s.add(name);
    }
    return s;
  }, [items]);

  const heading = SECTION_HEADING[activeSection] || "Section";

  // Right column is empty when the call has ended, consent isn't
  // being awaited, and the active section has no AI proposals and no
  // open manual / update / re-edit cards. In that state we collapse
  // the 340-px right slot and let the middle table use the full
  // width — otherwise the split reads as a big empty column past
  // Save & next.
  const hasRightSideCardsForActiveSection = (() => {
    switch (activeSection) {
      case "chief_complaints":
        return manualCardIds.length + updateCards.length + reeditCards.length > 0;
      case "diagnosis":
        return manualCardIdsDx.length + updateCardsDx.length + reeditCardsDx.length > 0;
      case "medicines":
        return manualCardIdsMed.length + reeditCardsMed.length + updateCardsMed.length > 0;
      case "hospitalisation":
        return manualCardIdsHosp.length + reeditCardsHosp.length + updateCardsHosp.length > 0;
      case "lab_investigations":
        return manualCardIdsLab.length + reeditCardsLab.length > 0;
      case "allergies":
        return manualCardIdsAlg.length + reeditCardsAlg.length + updateCardsAlg.length > 0;
      case "family_history":
        return manualCardIdsFh.length + reeditCardsFh.length + updateCardsFh.length > 0;
      case "addictions":
        return updateCardsAdd.length + reeditCardsAdd.length > 0;
      case "activity":
        return manualCardIdsAct.length + reeditCardsAct.length + updateCardsAct.length > 0;
      case "anthropometric":
        return manualCardsAnth.length + reeditCardsAnth.length > 0;
      case "daily_schedule":
        return reeditCardsDs.length > 0;
      case "food_preferences":
        // No right-rail card for food preferences (AI captures merge
        // into the middle-column form directly via FP_V2_AI_INJECT).
        // Return false so `rightColumnEmpty` collapses the 340-px slot.
        return false;
      case "drug_schedule":
        // The DrugScheduleEditor is only visible when a sheet is
        // open (add / update). While idle, collapse the rail so the
        // middle-column table spans full width like CC / Dx.
        return dsSheetOpen;
      case "patient_details":
        // Patient details rail only surfaces when a row click opens it.
        // Same idle-collapsed pattern as drug_schedule — otherwise
        // callEnded + no proposals + no open card would leave a 340px
        // empty column even when the reviewer isn't editing.
        return patientDetailsOpen;
      default:
        return false;
    }
  })();
  const rightColumnEmpty =
    // Past-transcript view: right column stays collapsed UNTIL the
    // reviewer clicks a row → then the S5-style card opens in it.
    (viewMode === "review" && !openedReviewId) ||
    (viewMode !== "review" && !!activeSection &&
      !awaitingConsent &&
      callEnded &&
      proposals.length === 0 &&
      !hasRightSideCardsForActiveSection);

  return (
    <div className="aic-shell">
      {!hideTabs && (
        <SectionTabs
          tabs={tabs}
          activeTab={activeTab}
          onSelect={onSelectTab}
          openCounts={openCountsBySection}
        />
      )}

      <div className={`aic-split${rightColumnEmpty ? " is-right-empty" : ""}`}>
        {/* Left half — the active section's own standalone renderer.
            Chief Complaints reuses the sidebar component as-is (must
            look identical inside vs outside the modal). Other sections
            still fall back to the generic table for now until they're
            individually integrated. */}
        <div className="aic-table-area">
          {activeSection == null ? (
            <div className="aic-empty">
              <Sparkle className="aic-empty-spark" />
              <p className="aic-empty-title">Select a section from the tabs above</p>
              <p className="aic-empty-sub">
                The section's existing view appears here; AI-proposed items
                appear on the right.
              </p>
            </div>
          ) : viewMode === "review" ? (
            /* Past-transcript view (reopened, non-live) — show ONLY the
               items THIS transcript captured, in the same per-section
               table layout the S5 Review screen uses. No standalone
               section embed here (the reviewer already saw the full
               patient list before opening; the transcript view is a
               focused summary of what THIS call added / modified). */
            forSection.length === 0 ? (
              <div className="aic-empty">
                <p className="aic-empty-title">Nothing captured</p>
                <p className="aic-empty-sub">
                  This transcript didn&rsquo;t add or modify any {heading.toLowerCase()} entries.
                </p>
              </div>
            ) : (
              <SectionCaptureTable
                section={activeSection}
                items={forSection}
                editingId={openedReviewId}
                patientId={patientId}
                onEdit={(it) => setOpenedReviewId(it.id)}
              />
            )
          ) : activeSection === "chief_complaints" ? (
            <ChiefComplaints patient={patient} capturedItems={forSection} />
          ) : activeSection === "diagnosis" ? (
            <Diagnosis patient={patient} capturedItems={forSection} />
          ) : activeSection === "medicines" ? (
            <Medicines patient={patient} capturedItems={forSection} />
          ) : activeSection === "hospitalisation" ? (
            <Hospitalisation patient={patient} capturedItems={forSection} />
          ) : activeSection === "lab_investigations" ? (
            <LabInvestigations patient={patient} capturedItems={forSection} />
          ) : activeSection === "allergies" ? (
            <Allergies patient={patient} capturedItems={forSection} onRowsSync={setAlgRows} />
          ) : activeSection === "family_history" ? (
            <FamilyHistory patient={patient} capturedItems={forSection} onRowsSync={setFhRows} />
          ) : activeSection === "addictions" ? (
            <Addictions patient={patient} capturedItems={forSection} />
          ) : activeSection === "activity" ? (
            <PhysicalActivity patient={patient} capturedItems={forSection} onRowsSync={setActRows} />
          ) : activeSection === "anthropometric" ? (
            <Anthropometric patient={patient} capturedItems={forSection} />
          ) : activeSection === "daily_schedule" ? (
            <DailySchedule patient={patient} capturedItems={forSection} />
          ) : activeSection === "food_preferences" ? (
            /* Whole-doc PUT resource — the middle column is just a
               read-through of the persisted state. Staged AI items
               live on the right column as separate confirm cards
               (no chip-level peach overlay). */
            <FoodPreference patient={patient} />
          ) : activeSection === "patient_details" ? (
            /* Patient details in transcript mode — Wave 2. Renders the
               standalone table with staged captures overlaid: fields
               with a pending update show peach + (for AI captures) a
               sparkle. Reviewer clicks any row → right column opens
               the 5-field form seeded from the staged overlay; saving
               UPSERTS onto the single "patient_details:main" captured
               item. Commit happens later at Review Save & next.
               `onEdit` bypasses the PD_V2_EDIT event bus so the
               standalone shell's dormant PatientDetailsEditor listener
               doesn't fight for the same open state. */
            <PatientDetails
              patient={patient}
              capturedItems={forSection}
              onEdit={openPatientDetailsRail}
            />
          ) : activeSection === "drug_schedule" ? (
            /* Drug schedule captures ride the standalone view — the
               reviewer picks any unresolved catalog match on the
               right-column edit sheet. Transcript-mode overlay in
               DrugSchedule.js is deferred for a follow-up (the AI's
               drug_schedule tool emits a single-plan atom that the
               ReviewSave table renders cleanly on its own). */
            <DrugSchedule patient={patient} capturedItems={forSection} transcriptMode />
          ) : (
            <div className="aic-tblcard">
              <header className="aic-tblcard-head">
                <h3 className="aic-tblcard-title">{heading}</h3>
                <span className="aic-tblcard-count">
                  {addedRows.length}
                  {addedRows.length === 1 ? " entry" : " entries"}
                </span>
              </header>
              <p className="aic-tblcard-empty">
                Section not integrated yet. This tab currently only shows the
                AI proposals on the right.
              </p>
            </div>
          )}
        </div>

        {/* Right half — Read-only proposal preview cards. No actions
            during the call; every decision is deferred to the review
            flow (post-call). Empty-state copy branches on whether
            consent has been captured — a silent right column is almost
            always because /captured is being 409-rejected by the server
            consent gate. */}
        <div className="aic-cards-area">
          {viewMode === "review" ? (
            openedReviewId ? (
              (() => {
                const captured = forSection.find((it) => it.id === openedReviewId);
                if (!captured) return null;
                return (
                  <CapturedItemCard
                    captured={captured}
                    section={activeSection}
                    patientId={patient && patient.id}
                    saving={false}
                    error=""
                    onSave={() => setOpenedReviewId("")}
                    onClose={() => setOpenedReviewId("")}
                    readOnly
                  />
                );
              })()
            ) : null
          ) : activeSection == null ? null : activeSection === "chief_complaints" ? (
            <>
              {/* Update cards from status-chip clicks surface at the very
                  top so the care manager doesn't miss the one they just
                  triggered. */}
              {updateCards.map(({ cardId, complaint }) => (
                <ComplaintForm
                  key={cardId}
                  formId={cardId}
                  editing={complaint}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCard(
                    cardId,
                    complaint,
                    ccBodyToCapturedUpdate(body, {
                      complaint: complaint.complaint,
                      complaintCode: complaint.complaintCode,
                    }),
                  )}
                  onClose={() => cancelUpdateCard(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Re-edit cards for already-staged captured items. Origin
                  drives the mode: staged-add → ai/manual (whichever the
                  original was), staged-update → update. Save updates the
                  same captured item in place. */}
              {reeditCards.map(({ cardId, captured, ccv2Record, initialPayloadOverride }) => {
                if (captured.operation === "update" && ccv2Record) {
                  // Synthesise `editing` from the underlying CC v2 record
                  // + the staged payload override so the update form
                  // pre-fills with the last-saved staged values.
                  const p = (captured.payload) || {};
                  const editing = {
                    ...ccv2Record,
                    current: {
                      vas:              typeof p.vas_min === "number" ? p.vas_min : (ccv2Record.current && ccv2Record.current.vas),
                      sinceWhen:        p.since_when || (ccv2Record.current && ccv2Record.current.sinceWhen),
                      description:      p.description || (ccv2Record.current && ccv2Record.current.description) || "",
                      consultedDoctor:  !!p.consulted_doctor,
                    },
                  };
                  // AI-origin update re-edit gets the brown AI theme; a
                  // manual-origin staged update stays on the update slate
                  // theme (indigo button + "Updating" chip). The single
                  // rule is: AI-populated → brown, everything else →
                  // indigo (with is-update reserved for the update chip).
                  const wasAiUpdate = (captured.confidence || 0) > 0;
                  return (
                    <ComplaintForm
                      key={cardId}
                      formId={cardId}
                      editing={editing}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCard(
                        cardId,
                        captured,
                        ccBodyToCapturedUpdate(body, {
                          complaint: ccv2Record.complaint,
                          complaintCode: ccv2Record.complaintCode,
                        }),
                      )}
                      onClose={() => cancelReeditCard(cardId)}
                      themeClass={wasAiUpdate ? "is-ai" : "is-update"}
                      menuClassName="ait-menu"
                    />
                  );
                }
                // Staged-add re-edit — same shape as the original AI/
                // manual card, but Save updates the existing captured
                // item via decide (payload override).
                const wasAi = (captured.confidence || 0) > 0;
                return (
                  <ComplaintForm
                    key={cardId}
                    formId={cardId}
                    initialPayload={initialPayloadOverride || captured.payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCard(cardId, captured, ccBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCard(cardId)}
                    themeClass={wasAi ? "is-ai" : "is-manual"}
                    menuClassName="ait-menu"
                    /* Re-opening an already-saved staged item: the complaint
                       + ICD were chosen on first Save and are frozen here. */
                    lockComplaint
                  />
                );
              })}
              {/* Manual (blank) cards next, above AI cards. */}
              {manualCardIds.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <ComplaintForm
                    formId={cardId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCard(cardId, ccBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCard(cardId)}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => (
                <ComplaintForm
                  key={it.id}
                  formId={it.id}
                  initialPayload={it.payload}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCard(it, ccBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                  menuClassName="ait-menu"
                />
              ))}
              {manualCardIds.length === 0 && proposals.length === 0 && updateCards.length === 0 && reeditCards.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add complaints</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "diagnosis" ? (
            <>
              {updateCardsDx.map(({ cardId, diagnosis: d }) => (
                <DiagnosisForm
                  key={cardId}
                  formId={cardId}
                  editing={d}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardDx(
                    cardId,
                    d,
                    dxBodyToCapturedUpdate(body, {
                      diagnosis:     d.diagnosis,
                      diagnosisCode: d.diagnosisCode,
                    }),
                  )}
                  onClose={() => cancelUpdateCardDx(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {reeditCardsDx.map(({ cardId, captured, dxv2Record, initialPayloadOverride }) => {
                if (captured.operation === "update" && dxv2Record) {
                  const p = (captured.payload) || {};
                  const editing = {
                    ...dxv2Record,
                    ongoing: p.treatment_status
                      ? String(p.treatment_status).toLowerCase() === "ongoing"
                      : !!dxv2Record.ongoing,
                    current: {
                      sinceWhen:       p.since_when || (dxv2Record.current && dxv2Record.current.sinceWhen),
                      description:     p.description || (dxv2Record.current && dxv2Record.current.description) || "",
                      consultedDoctor: !!p.consulted_doctor,
                    },
                  };
                  const wasAiUpdate = (captured.confidence || 0) > 0;
                  return (
                    <DiagnosisForm
                      key={cardId}
                      formId={cardId}
                      editing={editing}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardDx(
                        cardId,
                        captured,
                        dxBodyToCapturedUpdate(body, {
                          diagnosis:     dxv2Record.diagnosis,
                          diagnosisCode: dxv2Record.diagnosisCode,
                        }),
                      )}
                      onClose={() => cancelReeditCardDx(cardId)}
                      themeClass={wasAiUpdate ? "is-ai" : "is-update"}
                      menuClassName="ait-menu"
                    />
                  );
                }
                const wasAi = (captured.confidence || 0) > 0;
                return (
                  <DiagnosisForm
                    key={cardId}
                    formId={cardId}
                    initialPayload={initialPayloadOverride || captured.payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardDx(cardId, captured, dxBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardDx(cardId)}
                    themeClass={wasAi ? "is-ai" : "is-manual"}
                    menuClassName="ait-menu"
                    /* Re-opening an already-saved staged item: the diagnosis
                       + ICD were chosen on first Save and are frozen here. */
                    lockName
                  />
                );
              })}
              {manualCardIdsDx.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <DiagnosisForm
                    formId={cardId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardDx(cardId, dxBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardDx(cardId)}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => (
                <DiagnosisForm
                  key={it.id}
                  formId={it.id}
                  initialPayload={it.payload}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardDx(it, dxBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                  menuClassName="ait-menu"
                />
              ))}
              {manualCardIdsDx.length === 0 && proposals.length === 0 && updateCardsDx.length === 0 && reeditCardsDx.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add diagnosis</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "medicines" ? (
            <>
              {/* Update cards for existing Med v2 rows the reviewer
                  clicked in the middle-column table (medicine name /
                  STATUS chip → MED_V2_STATUS event). MedicineStatusForm
                  keeps its internal service call for save on this Wave-
                  A cut — status transitions on an already-persisted
                  medicine don't need transcript-side staging. */}
              {updateCardsMed.map(({ cardId, medicine }) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <MedicineStatusForm
                    formId={cardId}
                    patientId={patientId}
                    editing={medicine}
                    // MedicineStatusForm requires the full sheet-stack
                    // callback set. In transcript mode we don't share a
                    // save-lock across the rail (only one status card
                    // open per medicine, and the save hits Medicine v2
                    // directly), so onSavingChange is a no-op here.
                    // onFilledChange still routes into the shared blank
                    // tracker so the guard treats an unmodified update
                    // card the same way it treats an add card.
                    onFilledChange={onCardFilledChange}
                    onSavingChange={() => {}}
                    onClose={() => setUpdateCardsMed((prev) => prev.filter((u) => u.cardId !== cardId))}
                  />
                </div>
              ))}
              {reeditCardsMed.map(({ cardId, captured, initialPayloadOverride }) => {
                const wasAiMed = (captured.confidence || 0) > 0;
                return (
                  <MedicineForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initialPayloadOverride || captured.payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardMed(cardId, captured, medBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardMed(cardId)}
                    themeClass={wasAiMed ? "is-ai" : "is-manual"}
                    lockName
                  />
                );
              })}
              {manualCardIdsMed.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <MedicineForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardMed(cardId, medBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardMed(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => (
                <MedicineForm
                  key={it.id}
                  formId={it.id}
                  patientId={patientId}
                  initialPayload={it.payload}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardMed(it, medBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                />
              ))}
              {manualCardIdsMed.length === 0 && proposals.length === 0 && reeditCardsMed.length === 0 && updateCardsMed.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add medicine</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "hospitalisation" ? (
            <>
              {/* Update cards for existing Hosp v2 records that the
                  reviewer clicked in the middle-column table. Same shape
                  as the CC update card — form opens in edit mode with the
                  record pre-filled; Save propose+decide-flows a staged
                  update on the transcript's captured buffer. */}
              {updateCardsHosp.map(({ cardId, record }) => (
                <HospitalisationForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardHosp(cardId, record, hospBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardHosp(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave A first-cut for hosp: same shape as meds — manual +
                  AI-proposal + staged-add re-edit. */}
              {reeditCardsHosp.map(({ cardId, captured, hospv2Record, initialPayloadOverride }) => {
                const wasAiHosp = (captured.confidence || 0) > 0;
                const initP = initialPayloadOverride || captured.payload;
                if (captured.operation === "update" && hospv2Record) {
                  return (
                    <HospitalisationForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={hospv2Record}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardHosp(cardId, captured, hospBodyToCapturedUpdate(body, { record: hospv2Record }))}
                      onClose={() => cancelReeditCardHosp(cardId)}
                      themeClass={wasAiHosp ? "is-ai" : "is-update"}
                      lockName
                    />
                  );
                }
                return (
                  <HospitalisationForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardHosp(cardId, captured, hospBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardHosp(cardId)}
                    themeClass={wasAiHosp ? "is-ai" : "is-manual"}
                    lockName
                  />
                );
              })}
              {manualCardIdsHosp.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <HospitalisationForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardHosp(cardId, hospBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardHosp(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => (
                <HospitalisationForm
                  key={it.id}
                  formId={it.id}
                  patientId={patientId}
                  initialPayload={it.payload}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardHosp(it, hospBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                />
              ))}
              {manualCardIdsHosp.length === 0 && proposals.length === 0 && reeditCardsHosp.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add hospitalisation</strong> on the left to
                      add a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "lab_investigations" ? (
            <>
              {/* Wave A first-cut for lab investigations: same shape as
                  meds / hosp — manual + AI-proposal + staged-add re-edit.
                  Update auto-route deferred. */}
              {reeditCardsLab.map(({ cardId, captured }) => {
                const wasAiLab = (captured.confidence || 0) > 0;
                const editing = labCapturedItemsToCardEditing([captured]);
                return (
                  <DigitiseManuallyCard
                    key={cardId}
                    formId={cardId}
                    editing={editing}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onAdd={(commit) => {
                      const payload = labCommitToPrimaryCapturedAdd(commit, appointmentId);
                      if (payload) saveReeditCardLab(cardId, captured, payload);
                    }}
                    onAddAnother={() => EventService.emit(LI_V2_EDIT, {})}
                    onClose={() => cancelReeditCardLab(cardId)}
                    themeClass={wasAiLab ? "is-ai" : "is-manual"}
                  />
                );
              })}
              {manualCardIdsLab.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <DigitiseManuallyCard
                    formId={cardId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onAdd={(commit) => {
                      const payload = labCommitToPrimaryCapturedAdd(commit, appointmentId);
                      if (payload) saveManualCardLab(cardId, payload);
                    }}
                    onAddAnother={() => EventService.emit(LI_V2_EDIT, {})}
                    onClose={() => cancelManualCardLab(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                const editing = labCapturedItemsToCardEditing([it]);
                return (
                  <DigitiseManuallyCard
                    key={it.id}
                    formId={it.id}
                    editing={editing}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onAdd={(commit) => {
                      const payload = labCommitToPrimaryCapturedAdd(commit, appointmentId);
                      if (payload) saveAiCardLab(it, payload);
                    }}
                    onAddAnother={() => EventService.emit(LI_V2_EDIT, {})}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                  />
                );
              })}
              {manualCardIdsLab.length === 0 && proposals.length === 0 && reeditCardsLab.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> Add investigation</strong> on the left to add
                      a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "allergies" ? (
            <>
              {/* Update cards for existing Allergy v2 rows the reviewer
                  clicked in the middle-column table. */}
              {updateCardsAlg.map(({ cardId, record }) => (
                <AllergyForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardAlg(cardId, record, algBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardAlg(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave B first-cut for allergies: same shape as
                  meds / hosp / lab — manual + AI-proposal + staged-add
                  re-edit. */}
              {reeditCardsAlg.map(({ cardId, captured, algv2Record, initialPayloadOverride }) => {
                const wasAiAlg = (captured.confidence || 0) > 0;
                const initP = initialPayloadOverride || captured.payload;
                if (captured.operation === "update" && algv2Record) {
                  return (
                    <AllergyForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={algv2Record}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardAlg(cardId, captured, algBodyToCapturedUpdate(body, { record: algv2Record }))}
                      onClose={() => cancelReeditCardAlg(cardId)}
                      themeClass={wasAiAlg ? "is-ai" : "is-update"}
                      lockName
                    />
                  );
                }
                return (
                  <AllergyForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAlg(cardId, captured, algBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardAlg(cardId)}
                    themeClass={wasAiAlg ? "is-ai" : "is-manual"}
                    lockName
                  />
                );
              })}
              {manualCardIdsAlg.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <AllergyForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardAlg(cardId, algBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardAlg(cardId)}
                    themeClass="is-manual"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // Dedup against existing Allergy v2 rows. AI proposal
                // whose name matches an existing row opens as an update
                // card on that record instead of a duplicate add.
                const propName = ((it.payload && (it.payload.name || it.payload.substance)) || "").trim().toLowerCase();
                const match = propName
                  ? (algRows || []).find((r) => (r.name || "").trim().toLowerCase() === propName)
                  : null;
                if (match) {
                  return (
                    <AllergyForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.payload}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "allergies", match.allergyId,
                        algBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      themeClass="is-ai"
                      lockName
                    />
                  );
                }
                return (
                  <AllergyForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.payload}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAlg(it, algBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                  />
                );
              })}
              {manualCardIdsAlg.length === 0 && proposals.length === 0 && reeditCardsAlg.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add allergy</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "addictions" ? (
            <>
              {/* Wave B first-cut for addictions: fixed 4-slot resource.
                  Update cards (from row-click) sit on top, then AI
                  proposals, then re-edit cards. No "add manually"
                  affordance — every card is an update of an existing
                  row. */}
              {updateCardsAdd.map(({ cardId, addiction }) => (
                <AddictionForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={addiction}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardAdd(cardId, addiction, addBodyToCapturedAdd(body))}
                  onClose={() => cancelUpdateCardAdd(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {reeditCardsAdd.map(({ cardId, captured }) => {
                const wasAiAdd = (captured.confidence || 0) > 0;
                const key = captured.payload && captured.payload.key;
                const seedEditing = key ? { key, label: key.charAt(0).toUpperCase() + key.slice(1) } : {};
                return (
                  <AddictionForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    editing={seedEditing}
                    initialPayload={captured.payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAdd(cardId, captured, addBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardAdd(cardId)}
                    themeClass={wasAiAdd ? "is-ai" : "is-manual"}
                    menuClassName="ait-menu"
                  />
                );
              })}
              {proposals.map((it) => {
                const key = it.payload && it.payload.key;
                const seedEditing = key ? { key, label: key.charAt(0).toUpperCase() + key.slice(1) } : {};
                return (
                  <AddictionForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    editing={seedEditing}
                    initialPayload={it.payload}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAdd(it, addBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                  />
                );
              })}
              {updateCardsAdd.length === 0 && proposals.length === 0 && reeditCardsAdd.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Click a
                      row on the left to record a manual update.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "family_history" ? (
            <>
              {/* Update cards for existing FH v2 relatives the reviewer
                  clicked in the middle-column table. */}
              {updateCardsFh.map(({ cardId, record }) => (
                <RelativeForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardFh(cardId, record, fhBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardFh(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave B first-cut for family history: same shape as
                  allergies — manual + AI-proposal + staged-add re-edit. */}
              {reeditCardsFh.map(({ cardId, captured, famv2Record, initialPayloadOverride }) => {
                const wasAiFh = (captured.confidence || 0) > 0;
                const initP = initialPayloadOverride || captured.payload;
                if (captured.operation === "update" && famv2Record) {
                  return (
                    <RelativeForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={famv2Record}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardFh(cardId, captured, fhBodyToCapturedUpdate(body, { record: famv2Record }))}
                      onClose={() => cancelReeditCardFh(cardId)}
                      themeClass={wasAiFh ? "is-ai" : "is-update"}
                      lockName
                      menuClassName="ait-menu"
                    />
                  );
                }
                return (
                  <RelativeForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardFh(cardId, captured, fhBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardFh(cardId)}
                    themeClass={wasAiFh ? "is-ai" : "is-manual"}
                    lockName
                    menuClassName="ait-menu"
                  />
                );
              })}
              {manualCardIdsFh.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <RelativeForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardFh(cardId, fhBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardFh(cardId)}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // Dedup by relationship. FH is not name-keyed like PA /
                // Alg — each relative is identified by relationship
                // (Father, Mother, ...) instead. For single-only
                // relationships (Father, Mother, grandparents), a
                // second entry can't coexist, so an AI proposal
                // targeting a taken slot opens as an update on that
                // record. Multi relationships (brother/sister/other)
                // always render as a fresh add — those can legitimately
                // repeat. Normalise the AI's raw string using the same
                // rules RelativeForm's seedRelationshipFromPayload
                // applies (lowercase, "_"/" " → "-").
                const raw = ((it.payload && (it.payload.relationship || it.payload.value)) || "").toString();
                const relKey = raw.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
                const match = relKey && isSingleRelationship(relKey)
                  ? (fhRows || []).find((r) => (r.relationship || "").toLowerCase() === relKey)
                  : null;
                if (match) {
                  return (
                    <RelativeForm
                      key={it.id}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.payload}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "family_history", match.relativeId,
                        fhBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      themeClass="is-ai"
                      lockName
                      menuClassName="ait-menu"
                    />
                  );
                }
                return (
                  <RelativeForm
                    key={it.id}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.payload}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardFh(it, fhBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                  />
                );
              })}
              {manualCardIdsFh.length === 0 && proposals.length === 0 && reeditCardsFh.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add family member</strong> on the left to
                      add a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "anthropometric" ? (
            <>
              {/* Wave C first-cut for anthropometric: grouped-parameter
                  model — 5 user-entered parameters (Height, Weight,
                  Blood group, Waist, Hip), plus 2 server-derived
                  (BMI, WHR). The AI proposes per-parameter values;
                  the reviewer confirms via the same ParamEditor the
                  standalone rail uses. Manual entry from the
                  transcript view lands here too (row-name click on the
                  embedded Anthropometric component). */}
              {reeditCardsAnth.map(({ cardId, captured }) => {
                const wasAiAnth = (captured.confidence || 0) > 0;
                return (
                  <AnthropometricForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={captured.payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAnth(cardId, captured, anthBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardAnth(cardId)}
                    themeClass={wasAiAnth ? "is-ai" : "is-manual"}
                  />
                );
              })}
              {manualCardsAnth.map(({ cardId, parameter }) => (
                <AnthropometricForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  initialPayload={{ parameter }}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveManualCardAnth(cardId, parameter, anthBodyToCapturedAdd(body))}
                  onClose={() => cancelManualCardAnth(cardId)}
                  themeClass="is-manual"
                />
              ))}
              {proposals.map((it) => (
                <AnthropometricForm
                  key={it.id}
                  formId={it.id}
                  patientId={patientId}
                  initialPayload={it.payload}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardAnth(it, anthBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                />
              ))}
              {manualCardsAnth.length === 0 && proposals.length === 0 && reeditCardsAnth.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Click a
                      parameter row on the left to record a manual measurement.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "food_preferences" ? (
            // No right-rail card for food preferences — AI captures
            // land directly in the middle-column FoodPreference form's
            // chip lists (see the FP_V2_AI_INJECT useEffect above).
            // The right column stays visually collapsed via the
            // `rightColumnEmpty` gate that already skips this branch
            // when there's nothing to show.
            null
          ) : activeSection === "daily_schedule" ? (
            <>
              {/* Wave C first-cut for daily schedule: AI proposals (one
                  card per captured timeline entry) + review re-edit.
                  Manual add stays in the embedded DailySchedule's own
                  EntryModal (opens through the shared portal above
                  this rail). */}
              {reeditCardsDs.map(({ cardId, captured }) => {
                const wasAiDs = (captured.confidence || 0) > 0;
                return (
                  <ScheduleEntryForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={captured.payload}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardDs(cardId, captured, dsBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardDs(cardId)}
                    themeClass={wasAiDs ? "is-ai" : "is-manual"}
                    lockName
                  />
                );
              })}
              {proposals.map((it) => (
                <ScheduleEntryForm
                  key={it.id}
                  formId={it.id}
                  patientId={patientId}
                  initialPayload={it.payload}
                  saving={!!saving[it.id]}
                  error={errors[it.id] || ""}
                  onSave={(body) => saveAiCardDs(it, dsBodyToCapturedAdd(body))}
                  onClose={() => discardAi(it)}
                  themeClass="is-ai"
                />
              ))}
              {proposals.length === 0 && reeditCardsDs.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add timeline entry</strong> on the left to
                      add a manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "activity" ? (
            <>
              {/* Update cards for existing PA v2 activities the reviewer
                  clicked in the middle-column table (both edit and
                  status-chip clicks route through the same event pair). */}
              {updateCardsAct.map(({ cardId, record }) => (
                <ActivityForm
                  key={cardId}
                  formId={cardId}
                  patientId={patientId}
                  editing={record}
                  saving={!!saving[cardId]}
                  error={errors[cardId] || ""}
                  onSave={(body) => saveUpdateCardAct(cardId, record, actBodyToCapturedUpdate(body, { record }))}
                  onClose={() => cancelUpdateCardAct(cardId)}
                  themeClass="is-update"
                  menuClassName="ait-menu"
                />
              ))}
              {/* Wave B first-cut for physical activity: same shape as
                  allergies / FH — manual + AI-proposal + staged-add
                  re-edit. */}
              {reeditCardsAct.map(({ cardId, captured, actv2Record, initialPayloadOverride }) => {
                const wasAiAct = (captured.confidence || 0) > 0;
                const initP = initialPayloadOverride || captured.payload;
                // Staged-update re-edit: seed the form with the live PA
                // v2 record so isEdit=true (skips the duplicate-name
                // check that fires when the row's own name looks like
                // a "new entry" collision) and pre-fills the fields
                // from the record. initialPayload still overlays the
                // reviewer's most recent staged edits on top of that.
                if (captured.operation === "update" && actv2Record) {
                  return (
                    <ActivityForm
                      key={cardId}
                      formId={cardId}
                      patientId={patientId}
                      editing={actv2Record}
                      initialPayload={initP}
                      saving={!!saving[cardId]}
                      error={errors[cardId] || ""}
                      onSave={(body) => saveReeditCardAct(cardId, captured, actBodyToCapturedUpdate(body, { record: actv2Record }))}
                      onClose={() => cancelReeditCardAct(cardId)}
                      themeClass={wasAiAct ? "is-ai" : "is-update"}
                      lockName
                      menuClassName="ait-menu"
                    />
                  );
                }
                return (
                  <ActivityForm
                    key={cardId}
                    formId={cardId}
                    patientId={patientId}
                    initialPayload={initP}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onSave={(body) => saveReeditCardAct(cardId, captured, actBodyToCapturedAdd(body))}
                    onClose={() => cancelReeditCardAct(cardId)}
                    themeClass={wasAiAct ? "is-ai" : "is-manual"}
                    lockName
                    menuClassName="ait-menu"
                  />
                );
              })}
              {manualCardIdsAct.map((cardId) => (
                <div
                  className="zy-sheet"
                  key={cardId}
                  ref={(node) => registerCardNode(cardId, node)}
                >
                  {nudge.id === cardId ? (
                    <span key={nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
                  ) : null}
                  <ActivityForm
                    formId={cardId}
                    patientId={patientId}
                    saving={!!saving[cardId]}
                    error={errors[cardId] || ""}
                    onFilledChange={onCardFilledChange}
                    onSave={(body) => saveManualCardAct(cardId, actBodyToCapturedAdd(body))}
                    onClose={() => cancelManualCardAct(cardId)}
                    themeClass="is-manual"
                    menuClassName="ait-menu"
                  />
                </div>
              ))}
              {proposals.map((it) => {
                // Dedup against existing PA v2 rows. When an AI proposal's
                // name matches an existing entry (case-insensitive), open
                // the card as an update on that record instead of a
                // duplicate add. Save routes through saveAiProposalAsUpdate
                // which discards the ADD proposal and stages a fresh
                // update captured item.
                const propName = ((it.payload && it.payload.name) || "").trim().toLowerCase();
                const match = propName
                  ? (actRows || []).find((r) => (r.name || "").trim().toLowerCase() === propName)
                  : null;
                // Key includes a hash of the payload so an agent-merge
                // update (payload changes on a still-open proposal card)
                // remounts the form with the new AI fields instead of
                // freezing at the first-mount initial state. Trade-off:
                // the reviewer's in-progress manual edits reset when
                // the AI updates its capture — matches the user's ask
                // of "keep updating the captured fields in card".
                const payloadKey = JSON.stringify(it.payload || {});
                if (match) {
                  return (
                    <ActivityForm
                      key={`${it.id}::${payloadKey}`}
                      formId={it.id}
                      patientId={patientId}
                      editing={match}
                      initialPayload={it.payload}
                      saving={!!saving[it.id]}
                      error={errors[it.id] || ""}
                      onSave={(body) => saveAiProposalAsUpdate(
                        it, "activity", match.activityId,
                        actBodyToCapturedUpdate(body, { record: match }),
                      )}
                      onClose={() => discardAi(it)}
                      themeClass="is-ai"
                      lockName
                      menuClassName="ait-menu"
                    />
                  );
                }
                return (
                  <ActivityForm
                    key={`${it.id}::${payloadKey}`}
                    formId={it.id}
                    patientId={patientId}
                    initialPayload={it.payload}
                    saving={!!saving[it.id]}
                    error={errors[it.id] || ""}
                    onSave={(body) => saveAiCardAct(it, actBodyToCapturedAdd(body))}
                    onClose={() => discardAi(it)}
                    themeClass="is-ai"
                    menuClassName="ait-menu"
                  />
                );
              })}
              {manualCardIdsAct.length === 0 && proposals.length === 0 && reeditCardsAct.length === 0 ? (
                awaitingConsent ? (
                  <div className="aic-empty aic-empty-slim aic-empty-warn">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">Consent required</p>
                    <p className="aic-empty-sub">
                      Click <strong>Consent captured</strong> in the purple banner
                      above once the patient has agreed. AI extraction is blocked
                      until then.
                    </p>
                  </div>
                ) : callEnded ? null : (
                  <div className="aic-empty aic-empty-slim">
                    <Sparkle className="aic-empty-spark" />
                    <p className="aic-empty-title">AI is listening…</p>
                    <p className="aic-empty-sub">
                      Drafts appear here as the conversation happens. Use
                      <strong> + Add activity</strong> on the left to add a
                      manual entry.
                    </p>
                  </div>
                )
              ) : null}
            </>
          ) : activeSection === "patient_details" ? (
            /* Right column — the same rail editor the standalone
               patient page uses. `onManualSave` diverts the form's
               submit into /captured (Wave 2), and `capturedItems`
               lets the form pre-fill from staged values so the
               reviewer sees whichever value will be committed.
               Controlled mode: open / focus / onClose pinned to the
               state we own at the CapturedSections level so a row
               click in the middle column reliably surfaces the form
               here. */
            <PatientDetailsEditor
              patient={patient}
              onManualSave={saveManualCardPatient}
              capturedItems={forSection}
              open={patientDetailsOpen}
              focusField={patientDetailsFocus}
              focusAt={patientDetailsFocusAt}
              onClose={closePatientDetailsRail}
              /* Peach + maroon theme when the staged capture came from
                 the AI (confidence > 0); slate/indigo default when
                 the reviewer opened the card manually. Same rule CC
                 uses via `themeClass`. */
              themeClass={(() => {
                const staged = forSection.find((it) => (it && it.state) !== "reviewed");
                if (staged && (staged.confidence || 0) > 0) return "is-ai";
                return "is-manual";
              })()}
            />
          ) : activeSection === "drug_schedule" ? (
            /* Drug schedule uses the standalone rail editor for both
               "+ Add schedule" and Active-plan row/status clicks — its
               event listeners (DS_V2_ADD / DS_V2_UPDATE) mount here so
               the middle-column DrugSchedule button/row triggers the
               same Add/Update sheets it does on the patient page.
               `onManualSave` diverts Add submits into /captured (state
               added_in_call) so Review Save & next commits them via
               writeDrugSchedule — matches CC's manual-card flow.
               Update sheet still saves directly (dose-taken tracking is
               a live-action on an already-persisted plan). */
            <DrugScheduleEditor
              patient={patient}
              onManualSave={saveManualCardDrug}
              onCapturedUpdate={updateManualCardDrug}
              stagedDrugNames={stagedDrugSchedNames}
              addThemeClass="is-manual"
            />
          ) : proposals.length === 0 ? (
            awaitingConsent ? (
              <div className="aic-empty aic-empty-slim aic-empty-warn">
                <Sparkle className="aic-empty-spark" />
                <p className="aic-empty-title">Consent required</p>
                <p className="aic-empty-sub">
                  Click <strong>Consent captured</strong> in the purple banner
                  above once the patient has agreed. AI extraction is blocked
                  until then.
                </p>
              </div>
            ) : callEnded ? null : (
              <div className="aic-empty aic-empty-slim">
                <Sparkle className="aic-empty-spark" />
                <p className="aic-empty-title">AI is listening…</p>
                <p className="aic-empty-sub">
                  Drafts appear here as the conversation happens. Nothing is
                  saved yet — review after the call ends.
                </p>
              </div>
            )
          ) : (
            proposals.map((it) => <ProposalCard key={it.id} item={it} />)
          )}
        </div>
      </div>
    </div>
  );
};

CapturedSections.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id:    PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  activeTab:       PropTypes.string.isRequired,
  onSelectTab:     PropTypes.func.isRequired,
  items:           PropTypes.array.isRequired,
  patient:         PropTypes.object,
  /** True during an active call while consentStatus !== "captured".
      Drives an alt empty-state copy on the right column so the user
      knows AI extraction is server-side blocked, not silently broken. */
  awaitingConsent: PropTypes.bool,
  callEnded: PropTypes.bool,
  /** All of the following are only used by the CC card save path. */
  patientId:         PropTypes.number,
  /** Currently-selected appointment on the patient screen. Forwarded
   *  through the LI transcript cards so Lab v2 rows land scoped to
   *  the right appointment. Falsy → the wire omits appointmentId. */
  appointmentId:     PropTypes.number,
  transcriptId:      PropTypes.string,
  transcriptService: PropTypes.object,
  version:           PropTypes.number,
  onVersionSync:     PropTypes.func,
  onItemsSync:       PropTypes.func,
  hideTabs:          PropTypes.bool,
  viewMode:          PropTypes.oneOf(["", "review"]),
  onOpenCountsChange: PropTypes.func,
};

CapturedSections.defaultProps = {
  hideTabs: false,
  viewMode: "",
  patient: null,
  awaitingConsent: false,
  callEnded: false,
  patientId: 0,
  appointmentId: 0,
  transcriptId: "",
  transcriptService: null,
  version: 0,
  onVersionSync: () => {},
  onItemsSync:   () => {},
  onOpenCountsChange: () => {},
};

export default CapturedSections;
